export namespace Schemas {
  // <Schemas>
  export type AddInventoryImportExtraFieldsRequest = { import_uuid: string; boat_detail_uuid: string };
  export type AddInventoryImportMappedFieldsRequest = {
    import_uuid: string;
    field_mappings: Array<{ name: string; mapped_variable: string }>;
  };
  export type AddLogImportExtraFieldsRequest = { import_uuid: string; trip_uuid: string };
  export type AddLogImportMappedFieldsRequest = {
    import_uuid: string;
    field_mappings: Array<{ name: string; mapped_variable: string }>;
  };
  export type AddTaskImportExtraFieldsRequest = { import_uuid: string; boat_detail_uuid: string };
  export type AddTaskImportMappedFieldsRequest = {
    import_uuid: string;
    field_mappings: Array<{ name: string; mapped_variable: string }>;
  };
  export type AddressResource = {
    id: string;
    street: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  export type Alert = Array<string>;
  export type AssignInventorySubLocationRequest = { uuid: string; expense_uuid: string; location_uuid: string };
  export type Block = Array<string>;
  export type BlockRequest = { blocker_uuid: string; blocker_type: 'checklist'; blockable_id: number };
  export type Boat = Array<string>;
  export type BoatDetails = Array<string>;
  export type BoatModel = Array<string>;
  export type Category = Array<string>;
  export type ChannelProviderType = 'expo';
  export type ChatRequest = {
    message: string;
    history?: (Array<{ role: 'user' | 'assistant'; content: string }> | null) | undefined;
  };
  export type CheckChecklistItemRequest = { uuid: string; item_uuid?: string | undefined };
  export type Checklist = Array<string>;
  export type ChecklistItem = Array<string>;
  export type ChecklistItemCheck = Array<string>;
  export type Checklistable = Array<string>;
  export type Comment = Array<string>;
  export type ConfirmFileUploadRequest = {
    file_path: string;
    filename: string;
    /**
     * Advisory: still required, but the stored type is derived from the
     * file extension.
     */
    mime_type: string;
    size: number;
    folder_uuid?: (string | null) | undefined;
    visibility?: (('public' | 'private') | null) | undefined;
  };
  export type Cost = Array<string>;
  export type Costable = Array<string>;
  export type CountryResource = { id: string; name: string; iso_code: string };
  export type CreateChecklistableRequest = {
    id: string;
    checklist_uuid: string;
    checklistable_uuid: string;
    checklistable_type: 'task' | 'trip' | 'log' | 'expense';
  };
  export type CreateCostableRequest = {
    id: string;
    cost_uuid: string;
    costable_uuid: string;
    costable_type: 'task' | 'trip' | 'log' | 'expense';
  };
  export type CreateExpensableRequest = {
    id: string;
    expense_uuid: string;
    expensable_uuid: string;
    expensable_type: 'task' | 'log';
    quantity?: (number | null) | undefined;
    quantity_action?: (('buy' | 'use') | null) | undefined;
    usage_value?: (number | null) | undefined;
  };
  export type CreateExportRequest = {
    module: 'boats' | 'logs' | 'costs' | 'tasks' | 'checklists' | 'alerts' | 'trips' | 'inventory';
    format: 'xlsx' | 'csv' | 'pdf' | 'gpx';
    boat_detail_uuid?: (string | null) | undefined;
    trip_uuid?: (string | null) | undefined;
    include_autologs?: (boolean | null) | undefined;
  };
  /**
   * Links one file to one record.
   *
   * Name the file with **either** `file_uuid` **or** `file_id`, never both and never
   * neither — a request carrying two identifiers may be naming two different files, so
   * it is refused rather than guessed at. The generated schema shows both as optional
   * because JSON Schema cannot express that pairing from a rule set.
   *
   * Prefer `file_uuid`. A file's uuid exists on the device the moment the row does,
   * before the backend has acknowledged it and while offline; the numeric id only
   * arrives once the file has synced, which is what forces the picker to disable
   * unsynced files and mobile to poll before attaching a fresh upload.
   *
   * **`file_id` is deprecated and will be removed.** It stays only because a client
   * that has not updated still sends it, and an old build cannot be fixed by deploying
   * the backend: dropping it would park every attachment those users create at 200 with
   * null, silently. It can go once the released clients all send the uuid and installed
   * mobile builds have rolled over. Note that removing it from *this request* is the
   * whole of that change — the `fileables.file_id` column is separate and stays either
   * way, because the legacy L8 backend writes it and the read joins key off it.
   */
  export type CreateFileableRequest = {
    id: string;
    /**
     * Links one file to one record.
     *
     * Name the file with **either** `file_uuid` **or** `file_id`, never both and never
     * neither — a request carrying two identifiers may be naming two different files, so
     * it is refused rather than guessed at. The generated schema shows both as optional
     * because JSON Schema cannot express that pairing from a rule set.
     *
     * Prefer `file_uuid`. A file's uuid exists on the device the moment the row does,
     * before the backend has acknowledged it and while offline; the numeric id only
     * arrives once the file has synced, which is what forces the picker to disable
     * unsynced files and mobile to poll before attaching a fresh upload.
     *
     * **`file_id` is deprecated and will be removed.** It stays only because a client
     * that has not updated still sends it, and an old build cannot be fixed by deploying
     * the backend: dropping it would park every attachment those users create at 200 with
     * null, silently. It can go once the released clients all send the uuid and installed
     * mobile builds have rolled over. Note that removing it from *this request* is the
     * whole of that change — the `fileables.file_id` column is separate and stays either
     * way, because the legacy L8 backend writes it and the read joins key off it.
     */
    file_uuid?: string | undefined;
    /**
     * Links one file to one record.
     *
     * Name the file with **either** `file_uuid` **or** `file_id`, never both and never
     * neither — a request carrying two identifiers may be naming two different files, so
     * it is refused rather than guessed at. The generated schema shows both as optional
     * because JSON Schema cannot express that pairing from a rule set.
     *
     * Prefer `file_uuid`. A file's uuid exists on the device the moment the row does,
     * before the backend has acknowledged it and while offline; the numeric id only
     * arrives once the file has synced, which is what forces the picker to disable
     * unsynced files and mobile to poll before attaching a fresh upload.
     *
     * **`file_id` is deprecated and will be removed.** It stays only because a client
     * that has not updated still sends it, and an old build cannot be fixed by deploying
     * the backend: dropping it would park every attachment those users create at 200 with
     * null, silently. It can go once the released clients all send the uuid and installed
     * mobile builds have rolled over. Note that removing it from *this request* is the
     * whole of that change — the `fileables.file_id` column is separate and stays either
     * way, because the legacy L8 backend writes it and the read joins key off it.
     */
    file_id?: number | undefined;
    fileable_uuid: string;
    fileable_type: 'cost' | 'task' | 'expense' | 'log' | 'trip' | 'boat_details' | 'checklist';
  };
  export type CreateTaxonomableRequest = {
    id: string;
    taxonomy_uuid: string;
    taxonomable_uuid: string;
    taxonomable_type: 'boat';
  };
  export type CurrencyResource = { id: string; iso_code: string; name: string; symbol: string; is_default: string };
  /**
   * Registering or removing a single device token.
   *
   * Extends FormRequest rather than BaseFormRequest on purpose. BaseFormRequest turns a
   * failure into a SyncError and nulls the offending field instead of returning 422 —
   * correct for offline PowerSync writes, wrong here: a malformed token would silently
   * become null and the write would carry on against nothing.
   */
  export type DeviceTokenRequest = { provider_id: ChannelProviderType; token: string };
  export type Expensable = Array<string>;
  export type ExportResource = {
    uuid: string;
    module: string;
    boat_detail_uuid: string;
    trip_uuid: string;
    include_autologs: string;
    format: string;
    status: string;
    download_url: string;
    signed_download_url: string;
    expires_at: string;
    error_message: string;
    created_at: string;
  };
  /**
   * The kind of thing a favorite points at.
   * | |
   * |---|
   * | `boat` <br/> A public boat, identified by its Sanity document id. |
   */
  export type FavoritableType = 'boat';
  export type FavoriteRequest = {
    uuid?: string | undefined;
    /**
     * What is being favorited.
     * | |
     * |---|
     * | `boat` <br/> A public boat, identified by its Sanity document id. |
     */
    favoritable_type: FavoritableType;
    /**
     * Identifier of the favorited resource. For `boat`, its Sanity document id. A draft
     * id is rejected: only published documents can be favorited.
     */
    favoritable_ref: string;
  };
  export type FavoriteResource = {
    uuid: string;
    favoritable_type: string;
    favoritable_ref: string;
    created_at: string;
  };
  export type Field = Array<string>;
  export type FieldValue = Array<string>;
  export type File = Array<string>;
  export type Fileable = Array<string>;
  export type Folder = Array<string>;
  export type FollowRequest = { uuid?: string | undefined; followable_uuid: string; followable_type: 'checklist' };
  export type Import = Array<string>;
  export type Inventory = Array<string>;
  export type InventorySubLocation = Array<string>;
  export type Location = Array<string>;
  export type Log = Array<string>;
  /**
   * A number formatting symbol. The decimal symbol and the digit grouping symbol must differ.
   */
  export type NumberSymbol = '.' | ',';
  export type PresignFileRequest = {
    filename: string;
    /**
     * Advisory: still required, but the signed content type is derived from the
     * file extension, never from this value.
     */
    mime_type: string;
    size: number;
    folder_uuid?: (string | null) | undefined;
  };
  export type RelatedUserResource = { id: string; email: string; name: string; surname: string };
  export type TimezoneResource = { id: string; name: string };
  export type SettingsResource = {
    unit: string;
    timezone_id: string;
    language_id: string;
    currency_id: string;
    base_currency_id: string;
    date_format: string;
    time_format: string;
    first_day_of_week: number;
    decimal_symbol: string;
    digit_grouping_symbol: string;
    currency: CurrencyResource | null;
    base_currency: CurrencyResource | null;
    timezone: TimezoneResource | null;
  };
  export type SetupUserRequest = {
    name?: string | undefined;
    surname?: string | undefined;
    currency_id: number;
    base_currency_id: number;
    timezone_id: number;
    first_day_of_week: '0' | '1';
    time_format: 'HH:mm' | 'H:mm' | 'h:mm A' | 'hh:mm A';
    date_format: 'DD/MM/Y' | 'Y-MM-DD' | 'D/M/Y' | 'D/M/YY' | 'DD/MM/YY' | 'D.M.YY' | 'MMM D YYYY' | 'MM/D/YYYY';
    unit: 'imperial' | 'metric';
    decimal_symbol?: NumberSymbol | undefined;
    digit_grouping_symbol?: NumberSymbol | undefined;
  };
  export type StoreCommentRequest = {
    text: string;
    commentable_type: 'checklist';
    commentable_uuid: string;
    parent_uuid?: (string | null) | undefined;
  };
  export type Task = Array<string>;
  export type Taxonomable = Array<string>;
  export type Trip = Array<string>;
  /**
   * Every field is required because every column is NOT NULL in production. The mutation this
   * replaces declared `country` optional, but its resolver read the key unconditionally and the
   * column rejects null, so that path could only ever have failed.
   */
  export type UpdateAddressRequest = {
    street: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  export type UpdateAlertRequest = {
    uuid: string;
    active?: boolean | undefined;
    marked_as_done_at?: string | undefined;
  };
  export type UpdateCommentRequest = { text: string };
  export type UpdateFileRequest = {
    uuid: string;
    boat_detail_uuid?: (string | null) | undefined;
    name?: string | undefined;
    filename?: (string | null) | undefined;
    issued_by?: (string | null) | undefined;
    notes?: (string | null) | undefined;
    is_enabled?: (boolean | null) | undefined;
    folder_uuid?: (string | null) | undefined;
    expiration_date?: (string | null) | undefined;
    expiration_date_time_unit?: (number | null) | undefined;
    expiration_date_time_unit_type?: (('d' | 'w' | 'm' | 'y') | null) | undefined;
    has_expiration_date_reminder?: (boolean | null) | undefined;
    visibility?: (('public' | 'private') | null) | undefined;
    issuance_date?: (string | null) | undefined;
    alt?: (string | null) | undefined;
    description?: (string | null) | undefined;
    custom_properties?: (string | null) | undefined;
  };
  /**
   * Identity only. The currency ids this used to accept are preferences and belong to
   * PATCH /users/settings, which is also where the base-currency change dispatches the
   * cost-rate recompute.
   */
  export type UpdateProfileRequest = Partial<{ name: string; surname: string; birthday: string | null }>;
  export type UpdateSettingsRequest = Partial<{
    timezone_id: number | null;
    unit: 'imperial' | 'metric';
    language_id: number | null;
    currency_id: number | null;
    base_currency_id: number | null;
    first_day_of_week: '0' | '1';
    time_format: 'HH:mm' | 'H:mm' | 'h:mm A' | 'hh:mm A';
    date_format: 'DD/MM/Y' | 'Y-MM-DD' | 'D/M/Y' | 'D/M/YY' | 'DD/MM/YY' | 'D.M.YY' | 'MMM D YYYY' | 'MM/D/YYYY';
    decimal_symbol: NumberSymbol;
    digit_grouping_symbol: NumberSymbol;
  }>;
  export type UploadImportFileRequest = { import_file: string };
  export type UpsertBoatDetailsRequest = {
    uuid: string;
    created_at?: string | undefined;
    enabled?: boolean | undefined;
    name?: string | undefined;
    flag?: string | undefined;
    year?: string | undefined;
    description?: (string | null) | undefined;
    acquisition_date?: (string | null) | undefined;
    acquisition_type?: (('new' | 'used') | null) | undefined;
    hull_sn?: (string | null) | undefined;
    mmsi?: (string | null) | undefined;
    callsign?: (string | null) | undefined;
    epirb?: (string | null) | undefined;
    in_reach_tracker_id?: (string | null) | undefined;
    spot_tracker_id?: (string | null) | undefined;
    satellite_phone?: (string | null) | undefined;
    mobile_phone?: (string | null) | undefined;
    engine_battery_voltage?: (never | null) | undefined;
    engine_battery_capacity?: (number | null) | undefined;
    service_battery_voltage?: (never | null) | undefined;
    service_battery_capacity?: (number | null) | undefined;
    battery_power?: (number | null) | undefined;
    inverter_power?: (number | null) | undefined;
    solar_power?: (number | null) | undefined;
    wind_power?: (number | null) | undefined;
    dg_power?: (number | null) | undefined;
    service_loads?: (number | null) | undefined;
    tender_name?: (string | null) | undefined;
    tender_vendor?: (string | null) | undefined;
    tender_model?: (string | null) | undefined;
    tender_length?: (number | null) | undefined;
    image_uuid?: (string | null) | undefined;
    location?: (string | null) | undefined;
    price?: (number | null) | undefined;
    currency?: (string | null) | undefined;
    is_vat_paid?: (boolean | null) | undefined;
    boat_uuid?: (string | null) | undefined;
    strapi_ref_document_id?: (string | null) | undefined;
    boat_model_ref?: (string | null) | undefined;
    subscription_id?: (number | null) | undefined;
  };
  export type UpsertBoatModelRequest = {
    /**
     * Minted by the client, like every other row in this app. `boat_models` is a
     * PowerSync table, so a backend-minted id would mean an online round-trip
     * before a boat could be created and no offline submission at all. This uuid
     * is the Sanity `_id`.
     */
    uuid: string;
    created_at?: string | undefined;
    /**
     * PROVISIONAL. MDC-4408 specifies validation against a json schema shared
     * with the frontend, and that schema is MDC-4409's deliverable -- it does not
     * exist yet. What is here is the subset the backend needs regardless of how
     * the rest of the document settles: the three fields the denormalised columns
     * are derived from, plus `unit`, which decides what every length on the model
     * means and so cannot be left to Studio's default. Anything else the client sends is stored untouched. That is deliberate --
     * the resolver reads `boat_data` back verbatim, so an unknown key is the
     * frontend's business, not a reason to reject a submission.
     *
     * Keys are the Sanity document's own, camelCase and all, because `boat_data`
     * holds the model in the resolved shape the Sanity schema describes. The
     * denormalised *columns* keep Postgres naming: `year_first` the column comes
     * from `yearFirst` the key, in BoatModelDataSchema::denormalise().
     *
     * Spelled out here rather than delegated to BoatModelDataSchema, even though
     * that would read better: Scramble builds api.json by static analysis and
     * emits no requestBody at all for a rules() it cannot evaluate, so a method
     * call here costs the frontend its generated request type for this endpoint.
     */
    boat_data: {
      /**
       * 100, not 191: that is the Sanity schema's own rule on `boat.model`. A
       * longer value would store fine here and then block the moderator from
       * publishing, which is a dead end the submitter never sees.
       */
      model: string;
      /**
       * Carries the Sanity `_id` when the user picked an existing manufacturer and
       * only a name when they typed one. MDC-4410 needs the `_id` to build the
       * reference; without it a manufacturer document has to be created first, or
       * the field left for the moderator.
       */
      manufacturer: {
        /**
         * Carries the Sanity `_id` when the user picked an existing manufacturer and
         * only a name when they typed one. MDC-4410 needs the `_id` to build the
         * reference; without it a manufacturer document has to be created first, or
         * the field left for the moderator.
         */
        _id?: (string | null) | undefined;
        name: string;
      };
      unit: 'metric' | 'imperial';
      /**
       * PROVISIONAL. MDC-4408 specifies validation against a json schema shared
       * with the frontend, and that schema is MDC-4409's deliverable -- it does not
       * exist yet. What is here is the subset the backend needs regardless of how
       * the rest of the document settles: the three fields the denormalised columns
       * are derived from, plus `unit`, which decides what every length on the model
       * means and so cannot be left to Studio's default. Anything else the client sends is stored untouched. That is deliberate --
       * the resolver reads `boat_data` back verbatim, so an unknown key is the
       * frontend's business, not a reason to reject a submission.
       *
       * Keys are the Sanity document's own, camelCase and all, because `boat_data`
       * holds the model in the resolved shape the Sanity schema describes. The
       * denormalised *columns* keep Postgres naming: `year_first` the column comes
       * from `yearFirst` the key, in BoatModelDataSchema::denormalise().
       *
       * Spelled out here rather than delegated to BoatModelDataSchema, even though
       * that would read better: Scramble builds api.json by static analysis and
       * emits no requestBody at all for a rules() it cannot evaluate, so a method
       * call here costs the frontend its generated request type for this endpoint.
       */
      yearFirst?: (number | null) | undefined;
    };
  };
  export type UpsertBoatRequest = {
    uuid: string;
    created_at?: string | undefined;
    is_enabled?: boolean | undefined;
    status?: boolean | undefined;
    unit?: ('metric' | 'imperial') | undefined;
    model?: string | undefined;
    year_first?: (string | null) | undefined;
    year_last?: (string | null) | undefined;
    version?: (string | null) | undefined;
    reference_price?: (number | null) | undefined;
    currency?: (string | null) | undefined;
    slug?: (string | null) | undefined;
    description?: (string | null) | undefined;
    meta_title?: (string | null) | undefined;
    meta_description?: (string | null) | undefined;
    type?: ('sail' | 'power' | 'unpowered' | 'pwc' | 'other') | undefined;
    hull_type?: ('monohull' | 'catamaran' | 'trimaran' | 'pontoon' | 'other') | undefined;
    hull_material?:
      | (
          | 'wood'
          | 'iron_and_steel'
          | 'aluminium'
          | 'cupronickel'
          | 'fiberglass'
          | 'composite_material'
          | 'ferrocement'
          | 'carbon_fiber'
          | 'hypalon'
          | 'roplene'
          | 'pvc'
          | 'other'
        )
      | undefined;
    keel_type?: (('fin' | 'bilge' | 'centerboard' | 'daggerboard' | 'bulb' | 'wing' | 'other') | null) | undefined;
    cabins?: (number | null) | undefined;
    berths?: (number | null) | undefined;
    heads?: (number | null) | undefined;
    top_speed?: (number | null) | undefined;
    cruising_speed?: (number | null) | undefined;
    mast?: (number | null) | undefined;
    mast_configuration?: (string | null) | undefined;
    loa?: number | undefined;
    lwl?: (number | null) | undefined;
    boa?: (number | null) | undefined;
    bcl?: (number | null) | undefined;
    bh?: (number | null) | undefined;
    headroom?: (number | null) | undefined;
    bdcl?: (number | null) | undefined;
    nom?: (number | null) | undefined;
    draft_max?: (number | null) | undefined;
    draft_min?: (number | null) | undefined;
    displacement?: (number | null) | undefined;
    tonnage_gross?: (number | null) | undefined;
    tonnage_net?: (number | null) | undefined;
    ballast_type?: (string | null) | undefined;
    ballast_weight?: (number | null) | undefined;
    helm?: (string | null) | undefined;
    rudder?: (string | null) | undefined;
    cockpit_location?: (string | null) | undefined;
    cockpit_type?: (string | null) | undefined;
    unsinkable?: (boolean | null) | undefined;
    trailerable?: (boolean | null) | undefined;
    designer_name?: (string | null) | undefined;
    designer_site?: (string | null) | undefined;
    exterior_design?: (string | null) | undefined;
    exterior_site?: (string | null) | undefined;
    interior_design?: (string | null) | undefined;
    interior_site?: (string | null) | undefined;
    rigging?: (string | null) | undefined;
    rotating_spars?: (boolean | null) | undefined;
    spars?: (string | null) | undefined;
    standing_rigging?: (string | null) | undefined;
    spreaders_level?: (number | null) | undefined;
    spreaders_angle?: (string | null) | undefined;
    upwind_sail_area?: (number | null) | undefined;
    downwind_sail_area?: (number | null) | undefined;
    manufacturer_uuid?: (string | null) | undefined;
    image_uuid?: (string | null) | undefined;
    ce_classification?: Array<number | null> | undefined;
    mainsail?: (Array<{ name: string; value: number }> | null) | undefined;
    headsail?: (Array<{ name: string; value: number }> | null) | undefined;
    spinnaker?: (Array<{ name: string; value: number }> | null) | undefined;
    genoa?: (Array<{ name: string; value: number }> | null) | undefined;
    fuel_tank?: (Array<{ capacity: number }> | null) | undefined;
    water_tank?: (Array<{ capacity: number }> | null) | undefined;
    holding_tank?: (Array<{ capacity: number }> | null) | undefined;
    water_heater?: (Array<{ capacity: number }> | null) | undefined;
    hull_appendages?: (Array<{ name: string; value: number }> | null) | undefined;
  };
  export type UpsertCategoryRequest = {
    uuid: string;
    name?: string | undefined;
    type?: ('expense' | 'checklist' | 'task' | 'log' | 'cost') | undefined;
    is_enabled?: (boolean | null) | undefined;
    created_at?: string | undefined;
  };
  export type UpsertChecklistItemRequest = {
    uuid: string;
    name?: string | undefined;
    checklist_uuid?: string | undefined;
    cloned_uuid?: (string | null) | undefined;
    position?: number | undefined;
    is_enabled?: boolean | undefined;
    created_at?: string | undefined;
  };
  export type UpsertChecklistRequest = {
    uuid: string;
    title?: string | undefined;
    description?: (string | null) | undefined;
    created_at?: string | undefined;
    published_at?: (string | null) | undefined;
    is_enabled?: (boolean | null) | undefined;
    cloned_uuid?: (string | null) | undefined;
    last_merged_at?: (string | null) | undefined;
    last_ignored_at?: (string | null) | undefined;
    image_uuid?: (string | null) | undefined;
    category_uuid?: (string | null) | undefined;
  };
  export type UpsertCostRequest = {
    uuid: string;
    boat_detail_uuid?: string | undefined;
    created_at?: string | undefined;
    is_enabled?: (boolean | null) | undefined;
    name?: string | undefined;
    amount?: number | undefined;
    quantity?: (number | null) | undefined;
    date?: string | undefined;
    currency_id?: number | undefined;
    vat_type?: ('inclusive' | 'exclusive') | undefined;
    type?: ('goods' | 'services') | undefined;
    supplier?: (string | null) | undefined;
    vat_percentage?: (number | null) | undefined;
    notes?: (string | null) | undefined;
    category_uuid?: (string | null) | undefined;
  };
  export type UpsertFieldRequest = {
    uuid: string;
    name?: string | undefined;
    type?: ('text' | 'alphanumeric' | 'number' | 'date' | 'pick_list') | undefined;
    module?: ('cost' | 'task' | 'expense' | 'log' | 'trip' | 'boat_details' | 'checklist' | 'file') | undefined;
    is_enabled?: (boolean | null) | undefined;
    created_at?: (string | null) | undefined;
    options?: (Array<string | null> | null) | undefined;
  };
  export type UpsertFieldValueRequest = {
    uuid: string;
    field_uuid?: string | undefined;
    record_uuid?: string | undefined;
    value?: string | undefined;
    is_enabled?: (boolean | null) | undefined;
    created_at?: (string | null) | undefined;
  };
  export type UpsertFolderRequest = {
    uuid: string;
    name?: string | undefined;
    parent_uuid?: (string | null) | undefined;
    created_at?: string | undefined;
    is_enabled?: (boolean | null) | undefined;
  };
  export type UpsertInventoryRequest = {
    uuid: string;
    name?: string | undefined;
    boat_detail_uuid?: string | undefined;
    category_uuid?: (string | null) | undefined;
    location_uuid?: (string | null) | undefined;
    type?: (('consumables' | 'asset') | null) | undefined;
    brand?: (string | null) | undefined;
    model?: (string | null) | undefined;
    part_number?: (string | null) | undefined;
    serial_number?: (string | null) | undefined;
    barcode?: (string | null) | undefined;
    vendor_page?: (string | null) | undefined;
    packing?: (('tin_can' | 'box' | 'bulk' | 'bottle') | null) | undefined;
    maintenance_needed?: (boolean | null) | undefined;
    notes?: (string | null) | undefined;
    voltage?: (number | null) | undefined;
    width?: (number | null) | undefined;
    height?: (number | null) | undefined;
    depth?: (number | null) | undefined;
    weight?: (number | null) | undefined;
    supplier?: (string | null) | undefined;
    supplier_contact?: (string | null) | undefined;
    supplier_url?: (string | null) | undefined;
    consumption?: (number | null) | undefined;
    acquisition_date?: (string | null) | undefined;
    expiration_date?: (string | null) | undefined;
    expiration_time_unit?: (number | null) | undefined;
    expiration_time_unit_type?: (('d' | 'w' | 'm' | 'y') | null) | undefined;
    has_expiration_reminder?: (boolean | null) | undefined;
    expiration_is_custom?: (boolean | null) | undefined;
    warranty_date?: (string | null) | undefined;
    warranty_time_unit?: (number | null) | undefined;
    warranty_time_unit_type?: (('d' | 'w' | 'm' | 'y') | null) | undefined;
    has_warranty_reminder?: (boolean | null) | undefined;
    warranty_is_custom?: (boolean | null) | undefined;
    usage_due_date?: (string | null) | undefined;
    due_date_time_unit?: (number | null) | undefined;
    due_date_time_unit_type?: (('d' | 'w' | 'm' | 'y') | null) | undefined;
    has_due_date_reminder?: (boolean | null) | undefined;
    usage_is_custom?: (boolean | null) | undefined;
    current_usage_value?: (number | null) | undefined;
    usage_threshold?: (number | null) | undefined;
    threshold_reminder_value?: (number | null) | undefined;
    has_threshold_reminder?: (boolean | null) | undefined;
    usage_recurrence?: (number | null) | undefined;
    threshold_recurrence_reminder?: (number | null) | undefined;
    is_recurring?: (boolean | null) | undefined;
    recurrence_type?: (('and' | 'or') | null) | undefined;
    usage_time_unit?: (number | null) | undefined;
    usage_time_unit_type?: (('d' | 'w' | 'm' | 'y') | null) | undefined;
    usage_recurrence_time_unit?: (number | null) | undefined;
    usage_recurrence_time_unit_type?: (('d' | 'w' | 'm' | 'y') | null) | undefined;
    has_usage_reminder?: (boolean | null) | undefined;
    quantity?: (number | null) | undefined;
    unit?: (('pieces' | 'weight' | 'volume') | null) | undefined;
    minimum_quantity?: (number | null) | undefined;
    minimum_quantity_reminder?: (number | null) | undefined;
    has_minimum_quantity_reminder?: (boolean | null) | undefined;
    created_at?: string | undefined;
    is_enabled?: (boolean | null) | undefined;
    linked_expenses_uuids?: (Array<string> | null) | undefined;
  };
  export type UpsertLocationRequest = {
    uuid: string;
    name?: string | undefined;
    is_sub_location?: (boolean | null) | undefined;
    created_at?: string | undefined;
    is_enabled?: (boolean | null) | undefined;
  };
  export type UpsertLogRequest = {
    name?: (string | null) | undefined;
    uuid: string;
    boat_detail_uuid?: (string | null) | undefined;
    category_uuid?: (string | null) | undefined;
    trip_uuid?: (string | null) | undefined;
    datetime?: string | undefined;
    created_at?: string | undefined;
    creation_type?: (('autologged' | 'manual') | null) | undefined;
    creator_agent_name?: (string | null) | undefined;
    is_enabled?: (boolean | null) | undefined;
    latitude?: (number | null) | undefined;
    longitude?: (number | null) | undefined;
    type?: ('general' | 'usage' | 'weather' | 'radio' | 'social') | undefined;
    event?: (('routine' | 'course' | 'landing' | 'emergency' | 'fault') | null) | undefined;
    heading?: (number | null) | undefined;
    cog?: (number | null) | undefined;
    helm?: (string | null) | undefined;
    speed?: (number | null) | undefined;
    sog?: (number | null) | undefined;
    log?: (number | null) | undefined;
    depth?: (number | null) | undefined;
    bar?: (number | null) | undefined;
    wind_speed?: (number | null) | undefined;
    wind_direction?: (number | null) | undefined;
    sky?: (number | null) | undefined;
    precipitation?: (('drizzle' | 'rain' | 'sleet' | 'snow' | 'graupel' | 'hail') | null) | undefined;
    sea_state?: (number | null) | undefined;
    visibility?: (number | null) | undefined;
    air_temp?: (number | null) | undefined;
    sea_temp?: (number | null) | undefined;
    battery1?: (number | null) | undefined;
    battery2?: (number | null) | undefined;
    boat_water?: (number | null) | undefined;
    fuel?: (number | null) | undefined;
    gas?: (number | null) | undefined;
    bilge?: (boolean | null) | undefined;
    channel?: (string | null) | undefined;
    station?: (string | null) | undefined;
    message?: (string | null) | undefined;
    sea_area?: (string | null) | undefined;
    imminent?: (string | null) | undefined;
    later?: (string | null) | undefined;
    outlook?: (string | null) | undefined;
    diesel_generator_status?: (boolean | null) | undefined;
    diesel_generator_hours?: (number | null) | undefined;
    diesel_generator_rpm?: (number | null) | undefined;
    diesel_generator_fuel_check?: (boolean | null) | undefined;
    diesel_generator_oil_check?: (boolean | null) | undefined;
    diesel_generator_water_check?: (boolean | null) | undefined;
    diesel_generator_exhaust_check?: (boolean | null) | undefined;
    sails?: (string | null) | undefined;
    notes?: (string | null) | undefined;
    caption?: (string | null) | undefined;
  };
  export type UpsertTaskRequest = {
    uuid: string;
    boat_detail_uuid?: string | undefined;
    created_at?: string | undefined;
    is_enabled?: (boolean | null) | undefined;
    name?: string | undefined;
    completed_at?: (string | null) | undefined;
    status?: (('not_started' | 'in_progress' | 'done' | 'cancelled') | null) | undefined;
    priority?: (('1' | '2' | '3') | null) | undefined;
    notes?: (string | null) | undefined;
    category_uuid?: (string | null) | undefined;
    due_date?: (string | null) | undefined;
    time_unit?: (number | null) | undefined;
    time_unit_type?: (('d' | 'w' | 'm' | 'y') | null) | undefined;
    has_notification?: (boolean | null) | undefined;
    usage_time_unit?: (number | null) | undefined;
    usage_time_unit_type?: (('d' | 'w' | 'm' | 'y') | null) | undefined;
    usage_recurrence_time_unit?: (number | null) | undefined;
    usage_recurrence_time_unit_type?: (('d' | 'w' | 'm' | 'y') | null) | undefined;
    has_usage_reminder?: (boolean | null) | undefined;
    usage_is_custom?: (boolean | null) | undefined;
    is_recurring?: (boolean | null) | undefined;
  };
  export type UpsertTripRequest = {
    /**
     * Basic fields
     */
    uuid: string;
    boat_detail_uuid?: string | undefined;
    created_at?: string | undefined;
    name?: string | undefined;
    is_enabled?: (boolean | null) | undefined;
    skipper_id?: number | undefined;
    crewman_id?: (number | null) | undefined;
    started_at?: string | undefined;
    closed_at?: (string | null) | undefined;
    finished_at?: (string | null) | undefined;
    origin_longitude?: number | undefined;
    origin_latitude?: number | undefined;
    destination_longitude?: (number | null) | undefined;
    destination_latitude?: (number | null) | undefined;
    notes?: (string | null) | undefined;
    image_uuid?: (string | null) | undefined;
  };
  export type UserResource = {
    id: string;
    name: string;
    surname: string;
    email: string;
    birthday: string;
    novu_id: string;
    pm_type: string;
    pm_last_four: string;
    address: AddressResource | null;
  };

  // </Schemas>
}

export namespace Endpoints {
  // <Endpoints>

  export type get_ActivityLog_getActivityLogs = {
    method: 'GET';
    path: '/activity-logs';
    requestFormat: 'json';
    parameters: never;
    responses: { 200: string; 500: { error: 'Failed to retrieve activity logs' } };
  };
  export type get_ActivityLog_getActivityLogsByBoat = {
    method: 'GET';
    path: '/activity-logs/{boatUuid}';
    requestFormat: 'json';
    parameters: {
      path: { boatUuid: string };
    };
    responses: { 200: string; 500: { error: 'Failed to retrieve activity logs' } };
  };
  export type get_Alert_index = {
    method: 'GET';
    path: '/alerts';
    requestFormat: 'json';
    parameters: {
      query: Partial<{
        page: number | null;
        page_size: number | null;
        sort_by: string | null;
        sort_order: string | null;
        search: string | null;
        filter: string | null;
        with_disabled: ('0' | '1' | 'true' | 'false') | null;
        boat_detail_uuid: string | null;
        type: string | null;
        priority: number | null;
        status: ('active' | 'disabled' | 'raised' | 'completed') | null;
        due_date_start: string | null;
        due_date_end: string | null;
      }>;
    };
    responses: {
      200: string;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: string; error: string };
    };
  };
  export type patch_Alert_update = {
    method: 'PATCH';
    path: '/alerts';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpdateAlertRequest;
    };
    responses: {
      200: Schemas.Alert;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the alert.'; error: string };
    };
  };
  export type get_Alert_show = {
    method: 'GET';
    path: '/alerts/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: string;
      404: { message: string };
      422: { message: string };
      500: { message: string; error: string };
    };
  };
  export type post_Block_block = {
    method: 'POST';
    path: '/blockers';
    requestFormat: 'json';
    parameters: {
      body: Schemas.BlockRequest;
    };
    responses: {
      201: Schemas.Block;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while blocking.'; error: string };
    };
  };
  export type delete_Block_unblock = {
    method: 'DELETE';
    path: '/blockers/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Unblocked successfully.' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: { message: 'Block not found.' };
      500: { message: 'An error occurred while unblocking.'; error: string };
    };
  };
  export type put_Boat_upsert = {
    method: 'PUT';
    path: '/boats';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertBoatRequest;
    };
    responses: {
      200: Schemas.Boat;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating or updating the boat.'; error: string };
    };
  };
  export type patch_Boat_update = {
    method: 'PATCH';
    path: '/boats';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertBoatRequest;
    };
    responses: {
      200: Schemas.Boat;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the boat.'; error: string };
    };
  };
  export type delete_Boat_delete = {
    method: 'DELETE';
    path: '/boats/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Boat deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the boat.'; error: string };
    };
  };
  export type get_BoatDetails_index = {
    method: 'GET';
    path: '/boat_details';
    requestFormat: 'json';
    parameters: {
      query: Partial<{
        page: number | null;
        page_size: number | null;
        sort_by: string | null;
        sort_order: string | null;
        search: string | null;
        filter: string | null;
        with_disabled: ('0' | '1' | 'true' | 'false') | null;
        type: string | null;
        status: ('0' | '1' | 'true' | 'false') | null;
        hull_type: string | null;
        hull_material: string | null;
        keel_type: string | null;
        flag: string | null;
        acquisition_type: string | null;
        year_min: number | null;
        year_max: number | null;
      }>;
    };
    responses: {
      200: string;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: string; error: string };
    };
  };
  export type put_BoatDetails_upsert = {
    method: 'PUT';
    path: '/boat_details';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertBoatDetailsRequest;
    };
    responses: {
      200: Schemas.BoatDetails;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating or updating the boat details.'; error: string };
    };
  };
  export type patch_BoatDetails_update = {
    method: 'PATCH';
    path: '/boat_details';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertBoatDetailsRequest;
    };
    responses: {
      200: Schemas.BoatDetails;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the boat details.'; error: string };
    };
  };
  export type get_BoatDetails_show = {
    method: 'GET';
    path: '/boat_details/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: string;
      404: { message: string };
      422: { message: string };
      500: { message: string; error: string };
    };
  };
  export type delete_BoatDetails_delete = {
    method: 'DELETE';
    path: '/boat_details/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Boat details deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the boat details.'; error: string };
    };
  };
  export type put_BoatModel_upsert = {
    method: 'PUT';
    path: '/boat_models';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertBoatModelRequest;
    };
    responses: {
      200: Schemas.BoatModel | (Schemas.BoatModel | null);
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while submitting the boat model.'; error: string };
    };
  };
  /**
   * A moderator pressing Publish in Studio sets it; taking a published model back down
   * clears it.
   *
   * This fires for every published boat mutation, including the whole existing catalogue,
   * so the overwhelming majority of calls are for documents with no row here. That is
   * not an error and must not be reported as one — an unknown id is a no-op, and the
   * response is always 200 so Sanity does not queue redeliveries for them.
   *
   * The event type is deliberately not trusted. Sanity delivers at least once and retries
   * a failed delivery twice at 30-second intervals, so a delete and a later create can
   * reach us out of order — and an unpublish fires *both*, since it deletes the published
   * document and creates a draft from its contents. Acting on the operation would make
   * that ordering decide whether a model reads as published. Instead the delivery is only
   * a signal to go and look: the handler asks Sanity what the current state of that one
   * document is and writes that. Every delivery for the same id then converges on the
   * same answer whatever order they arrive in, and a duplicate is free.
   *
   * The hourly reconciler is still the backstop for a delivery that never arrives at all.
   *
   * Signature verification happens in VerifySanityWebhook, before any of this runs.
   */
  export type post_BoatModelWebhook_handle = {
    method: 'POST';
    path: '/webhooks/sanity/boat-models';
    requestFormat: 'json';
    parameters: never;
    responses: {
      200: { received: boolean; published: boolean; unpublished: boolean };
      503: { received: boolean; error: 'Could not verify against Sanity' };
    };
  };
  export type get_Category_index = {
    method: 'GET';
    path: '/categories';
    requestFormat: 'json';
    parameters: {
      query: Partial<{
        page: number | null;
        page_size: number | null;
        sort_by: string | null;
        sort_order: string | null;
        search: string | null;
        filter: string | null;
        with_disabled: ('0' | '1' | 'true' | 'false') | null;
        type: string | null;
      }>;
    };
    responses: {
      200: string;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: string; error: string };
    };
  };
  export type put_Category_upsert = {
    method: 'PUT';
    path: '/categories';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertCategoryRequest;
    };
    responses: {
      200: Schemas.Category;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating or updating the category.'; error: string };
    };
  };
  export type patch_Category_update = {
    method: 'PATCH';
    path: '/categories';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertCategoryRequest;
    };
    responses: {
      200: Schemas.Category;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the category.'; error: string };
    };
  };
  export type get_Category_show = {
    method: 'GET';
    path: '/categories/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: string;
      404: { message: string };
      422: { message: string };
      500: { message: string; error: string };
    };
  };
  export type delete_Category_delete = {
    method: 'DELETE';
    path: '/categories/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Category deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the category.'; error: string };
    };
  };
  export type post_Chat_chat = {
    method: 'POST';
    path: '/assistant/chat';
    requestFormat: 'json';
    parameters: {
      body: Schemas.ChatRequest;
    };
    responses: {
      200: {
        reply: string;
        tool_calls: Array<any>;
        usage: { input_tokens: 0; output_tokens: 0 };
        quota: { limit: number; used: number; remaining: string; resets_at: string };
      };
      401: { message: 'Unauthenticated.' };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      429: { message: 'You have reached your monthly AI message limit.'; quota: string };
      500: { message: 'An error occurred while processing your message.'; error: string };
      503: { message: 'The assistant is busy. Please try again.' };
    };
  };
  export type get_Chat_connector = {
    method: 'GET';
    path: '/assistant/connector';
    requestFormat: 'json';
    parameters: never;
    responses: { 200: { enabled: boolean; eligible: string; url: string } };
  };
  export type get_Checklist_getFeaturedPublicChecklists = {
    method: 'GET';
    path: '/checklists/featured';
    requestFormat: 'json';
    parameters: {
      query: Partial<{
        page: number | null;
        page_size: number | null;
        sort_by: string | null;
        sort_order: string | null;
        search: string | null;
        filter: string | null;
        with_disabled: ('0' | '1' | 'true' | 'false') | null;
        category_uuid: string | null;
      }>;
    };
    responses: {
      200: string;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: string; error: string };
    };
  };
  export type get_Checklist_getPublicChecklist = {
    method: 'GET';
    path: '/checklists/public/{uuid}';
    requestFormat: 'json';
    parameters: {
      query: { uuid: string };
      path: { uuid: string };
    };
    responses: {
      200: Array<unknown> | null;
      404: { message: 'Checklist not found.' };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while fetching the checklist.'; error: string };
    };
  };
  export type get_Checklist_index = {
    method: 'GET';
    path: '/checklists';
    requestFormat: 'json';
    parameters: {
      query: Partial<{
        page: number | null;
        page_size: number | null;
        sort_by: string | null;
        sort_order: string | null;
        search: string | null;
        filter: string | null;
        with_disabled: ('0' | '1' | 'true' | 'false') | null;
        category_uuid: string | null;
        trip_uuid: string | null;
      }>;
    };
    responses: {
      200: string;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: string; error: string };
    };
  };
  export type put_Checklist_upsert = {
    method: 'PUT';
    path: '/checklists';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertChecklistRequest;
    };
    responses: {
      200: Schemas.Checklist;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating or updating the checklist.'; error: string };
    };
  };
  export type patch_Checklist_update = {
    method: 'PATCH';
    path: '/checklists';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertChecklistRequest;
    };
    responses: {
      200: Schemas.Checklist;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the checklist.'; error: string };
    };
  };
  export type get_Checklist_show = {
    method: 'GET';
    path: '/checklists/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: string;
      404: { message: string };
      422: { message: string };
      500: { message: string; error: string };
    };
  };
  export type delete_Checklist_delete = {
    method: 'DELETE';
    path: '/checklists/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Checklist deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the checklist.'; error: string };
    };
  };
  export type put_Checklist_createChecklistable = {
    method: 'PUT';
    path: '/checklistables';
    requestFormat: 'json';
    parameters: {
      body: Schemas.CreateChecklistableRequest;
    };
    responses: {
      200: Schemas.Checklistable;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating the checklistable.'; error: string };
    };
  };
  export type delete_Checklist_deleteChecklistable = {
    method: 'DELETE';
    path: '/checklistables/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Checklistable deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the checklistable.'; error: string };
    };
  };
  export type get_Checklist_getPublicChecklists = {
    method: 'GET';
    path: '/checklists/public';
    requestFormat: 'json';
    parameters: {
      query: Partial<{
        page: number | null;
        page_size: number | null;
        sort_by: string | null;
        sort_order: string | null;
        search: string | null;
        filter: string | null;
        with_disabled: ('0' | '1' | 'true' | 'false') | null;
        category_uuid: string | null;
      }>;
    };
    responses: {
      200: string;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: string; error: string };
    };
  };
  export type put_ChecklistItem_upsertItem = {
    method: 'PUT';
    path: '/checklist_items';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertChecklistItemRequest;
    };
    responses: {
      200: Schemas.ChecklistItem;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating or updating the checklist item.'; error: string };
    };
  };
  export type patch_ChecklistItem_updateItem = {
    method: 'PATCH';
    path: '/checklist_items';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertChecklistItemRequest;
    };
    responses: {
      200: Schemas.ChecklistItem;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the checklist item.'; error: string };
    };
  };
  export type delete_ChecklistItem_deleteItem = {
    method: 'DELETE';
    path: '/checklist_items/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Checklist item deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the checklist item.'; error: string };
    };
  };
  export type put_ChecklistItem_checkItem = {
    method: 'PUT';
    path: '/checklist_item_check';
    requestFormat: 'json';
    parameters: {
      body: Schemas.CheckChecklistItemRequest;
    };
    responses: {
      200: Schemas.ChecklistItemCheck;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while checking the checklist item.'; error: string };
    };
  };
  export type delete_ChecklistItem_uncheckItem = {
    method: 'DELETE';
    path: '/checklist_item_check/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Checklist item unchecked successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while unchecking the checklist item.'; error: string };
    };
  };
  export type post_ClerkWebhook_handle = {
    method: 'POST';
    path: '/webhooks/clerk';
    requestFormat: 'json';
    parameters: {
      body: Partial<{
        /**
         * Middleware has already verified the webhook and parsed the payload
         */
        webhook_payload: string;
        svix_id: string;
      }>;
    };
    responses: {
      200:
        | { message: 'Conflict: email already linked to a different account' }
        | {
            status: 'success';
            action: string;
            organization_id: string;
            message: 'Organization not kept in this database, nothing to do' | null;
          }
        | { message: 'Unhandled event type' }
        | { message: 'Already processed' };
      400: { message: 'Missing svix_id' };
      409: { message: 'Not ready, retry later' } | { message: 'Processing in progress' };
      500: { message: 'Processing failed' };
    };
  };
  export type post_Comment_store = {
    method: 'POST';
    path: '/comments';
    requestFormat: 'json';
    parameters: {
      body: Schemas.StoreCommentRequest;
    };
    responses: {
      201: Schemas.Comment;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating the comment.'; error: string };
    };
  };
  export type patch_Comment_update = {
    method: 'PATCH';
    path: '/comments/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };

      body: Schemas.UpdateCommentRequest;
    };
    responses: {
      200: Schemas.Comment;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: { message: 'Comment not found.' };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the comment.'; error: string };
    };
  };
  export type delete_Comment_delete = {
    method: 'DELETE';
    path: '/comments/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Comment deleted successfully.' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: { message: 'Comment not found.' };
      500: { message: 'An error occurred while deleting the comment.'; error: string };
    };
  };
  export type get_Cost_index = {
    method: 'GET';
    path: '/costs';
    requestFormat: 'json';
    parameters: {
      query: Partial<{
        page: number | null;
        page_size: number | null;
        sort_by: string | null;
        sort_order: string | null;
        search: string | null;
        filter: string | null;
        with_disabled: ('0' | '1' | 'true' | 'false') | null;
        boat_detail_uuid: string | null;
        category_uuid: string | null;
        type: string | null;
        record_type: ('task' | 'trip' | 'log' | 'expense') | null;
        record_uuid: string | null;
        trip_uuid: string | null;
        start_date: string | null;
        end_date: string | null;
        amount_min: number | null;
        amount_max: number | null;
      }>;
    };
    responses: {
      200: string;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: string; error: string };
    };
  };
  export type put_Cost_upsert = {
    method: 'PUT';
    path: '/costs';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertCostRequest;
    };
    responses: {
      200: Schemas.Cost;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating or updating the cost.'; error: string };
    };
  };
  export type patch_Cost_update = {
    method: 'PATCH';
    path: '/costs';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertCostRequest;
    };
    responses: {
      200: Schemas.Cost;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the cost.'; error: string };
    };
  };
  export type get_Cost_show = {
    method: 'GET';
    path: '/costs/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: string;
      404: { message: string };
      422: { message: string };
      500: { message: string; error: string };
    };
  };
  export type delete_Cost_delete = {
    method: 'DELETE';
    path: '/costs/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Cost deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the cost.'; error: string };
    };
  };
  export type put_Cost_createCostable = {
    method: 'PUT';
    path: '/costables';
    requestFormat: 'json';
    parameters: {
      body: Schemas.CreateCostableRequest;
    };
    responses: {
      200: Schemas.Costable;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating the costable relationship.'; error: string };
    };
  };
  export type delete_Cost_deleteCostable = {
    method: 'DELETE';
    path: '/costables/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Costable relationship deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the costable relationship.'; error: string };
    };
  };
  export type get_Country_index = {
    method: 'GET';
    path: '/countries';
    requestFormat: 'json';
    parameters: never;
    responses: {
      200: Array<Schemas.CountryResource>;
      422: { message: string };
      500: { message: string; error: string };
    };
  };
  export type get_Currency_index = {
    method: 'GET';
    path: '/currencies';
    requestFormat: 'json';
    parameters: never;
    responses: {
      200: Array<Schemas.CurrencyResource>;
      422: { message: string };
      500: { message: string; error: string };
    };
  };
  export type get_DeviceToken_index = {
    method: 'GET';
    path: '/notifications/device-tokens';
    requestFormat: 'json';
    parameters: {
      query: { provider_id: 'expo' };
    };
    responses: {
      200: string | { tokens: Array<string> };
      401: { message: 'Unauthenticated.' };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      503: { message: 'Notification service unavailable. Please try again.' };
    };
  };
  export type post_DeviceToken_store = {
    method: 'POST';
    path: '/notifications/device-tokens';
    requestFormat: 'json';
    parameters: {
      body: Schemas.DeviceTokenRequest;
    };
    responses: {
      200: string;
      401: { message: 'Unauthenticated.' };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      503: { message: 'Notification service unavailable. Please try again.' };
    };
  };
  export type delete_DeviceToken_destroy = {
    method: 'DELETE';
    path: '/notifications/device-tokens';
    requestFormat: 'json';
    parameters: {
      query: { provider_id: 'expo'; token: string };
    };
    responses: {
      200: string | { tokens: Array<string>; changed: boolean };
      401: { message: 'Unauthenticated.' };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      503: { message: 'Notification service unavailable. Please try again.' };
    };
  };
  export type post_Export_create = {
    method: 'POST';
    path: '/exports';
    requestFormat: 'json';
    parameters: {
      body: Schemas.CreateExportRequest;
    };
    responses: {
      202: { data: Schemas.ExportResource };
      401: { error: 'Unauthenticated.' };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while dispatching the export.' };
    };
  };
  export type get_Export_show = {
    method: 'GET';
    path: '/exports/{export}';
    requestFormat: 'json';
    parameters: {
      path: { export: number };
    };
    responses: {
      200: { data: Schemas.ExportResource };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: {
        /**
         * Error overview.
         */
        message: string;
      };
    };
  };
  export type get_Exports_download = {
    method: 'GET';
    path: '/exports/{export}/download';
    requestFormat: 'json';
    parameters: {
      path: { export: number };
    };
    responses: {
      200: Record<string, unknown>;
      404: {
        /**
         * Error overview.
         */
        message: string;
      };
    };
  };
  export type get_Favorite_index = {
    method: 'GET';
    path: '/favorites';
    requestFormat: 'json';
    parameters: {
      query: Partial<{
        /**
         * The kind of thing a favorite points at.
         * | |
         * |---|
         * | `boat` <br/> A public boat, identified by its Sanity document id. |
         */
        favoritable_type: 'boat';
      }>;
    };
    responses: {
      200: Array<Schemas.FavoriteResource>;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: string; error: string };
    };
  };
  export type post_Favorite_store = {
    method: 'POST';
    path: '/favorites';
    requestFormat: 'json';
    parameters: {
      body: Schemas.FavoriteRequest;
    };
    responses: {
      200: Schemas.FavoriteResource;
      201: Schemas.FavoriteResource;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while favoriting.'; error: string };
    };
  };
  export type delete_Favorite_destroy = {
    method: 'DELETE';
    path: '/favorites/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Removed from favorites successfully.' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while removing from favorites.'; error: string };
    };
  };
  export type put_Field_upsert = {
    method: 'PUT';
    path: '/fields';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertFieldRequest;
    };
    responses: {
      200: Schemas.Field;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating or updating the field.'; error: string };
    };
  };
  export type patch_Field_update = {
    method: 'PATCH';
    path: '/fields';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertFieldRequest;
    };
    responses: {
      200: Schemas.Field;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the field.'; error: string };
    };
  };
  export type delete_Field_delete = {
    method: 'DELETE';
    path: '/fields/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Field deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the field.'; error: string };
    };
  };
  export type put_Field_upsertValue = {
    method: 'PUT';
    path: '/field_values';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertFieldValueRequest;
    };
    responses: {
      200: Schemas.FieldValue;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating or updating the field value.'; error: string };
    };
  };
  export type patch_Field_updateValue = {
    method: 'PATCH';
    path: '/field_values';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertFieldValueRequest;
    };
    responses: {
      200: Schemas.FieldValue;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the field value.'; error: string };
    };
  };
  export type delete_Field_deleteValue = {
    method: 'DELETE';
    path: '/field_values/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Field value deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the field value.'; error: string };
    };
  };
  export type get_File_getPublicFile = {
    method: 'GET';
    path: '/files/public/{fileUuid}';
    requestFormat: 'json';
    parameters: {
      query: Partial<{ w: number | null; h: number | null; q: number | null; format: string | null }>;
      path: { fileUuid: string };
    };
    responses: {
      200: unknown;
      403: { message: string };
      404: { message: string };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while downloading the file.'; error: string };
    };
  };
  export type get_File_index = {
    method: 'GET';
    path: '/files';
    requestFormat: 'json';
    parameters: {
      query: Partial<{
        page: number | null;
        page_size: number | null;
        sort_by: string | null;
        sort_order: string | null;
        search: string | null;
        filter: string | null;
        with_disabled: ('0' | '1' | 'true' | 'false') | null;
        type: string | null;
        folder_uuid: string | null;
        boat_detail_uuid: string | null;
        is_filepicker: ('0' | '1' | 'true' | 'false') | null;
        start_date: string | null;
        end_date: string | null;
      }>;
    };
    responses: {
      200: string;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: string; error: string };
    };
  };
  export type patch_File_update = {
    method: 'PATCH';
    path: '/files';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpdateFileRequest;
    };
    responses: {
      200: Schemas.File;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the file.'; error: string };
    };
  };
  export type get_File_show = {
    method: 'GET';
    path: '/files/{uuid}/details';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: string;
      404: { message: string };
      422: { message: string };
      500: { message: string; error: string };
    };
  };
  export type post_File_presign = {
    method: 'POST';
    path: '/files/presign';
    requestFormat: 'json';
    parameters: {
      body: Schemas.PresignFileRequest;
    };
    responses: {
      200: { presigned_url: string; headers: string; file_path: string; expires_in: 900 };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while generating the upload URL.'; error: string };
    };
  };
  export type post_File_confirm = {
    method: 'POST';
    path: '/files/confirm';
    requestFormat: 'json';
    parameters: {
      body: Schemas.ConfirmFileUploadRequest;
    };
    responses: {
      201: string | Schemas.File;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while confirming the file upload.'; error: string };
    };
  };
  export type delete_File_delete = {
    method: 'DELETE';
    path: '/files/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'File deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the file.'; error: string };
    };
  };
  export type get_File_download = {
    method: 'GET';
    path: '/files/{fileUuid}';
    requestFormat: 'json';
    parameters: {
      query: Partial<{
        w: number | null;
        h: number | null;
        q: number | null;
        format: string | null;
        token: string;
        disposition: ('inline' | 'attachment') | null;
      }>;
      path: { fileUuid: string };
    };
    responses: {
      200: unknown;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: { message: 'File not found' };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { error: string; message: 'An error occurred while downloading the file.' };
    };
  };
  export type put_File_createFileable = {
    method: 'PUT';
    path: '/fileables';
    requestFormat: 'json';
    parameters: {
      body: Schemas.CreateFileableRequest;
    };
    responses: {
      200: Schemas.Fileable;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating the fileable relationship.'; error: string };
    };
  };
  export type delete_File_deleteFileable = {
    method: 'DELETE';
    path: '/fileables/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Fileable relationship deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the fileable relationship.'; error: string };
    };
  };
  export type get_Folder_index = {
    method: 'GET';
    path: '/folders';
    requestFormat: 'json';
    parameters: {
      query: Partial<{
        page: number | null;
        page_size: number | null;
        sort_by: string | null;
        sort_order: string | null;
        search: string | null;
        filter: string | null;
        with_disabled: ('0' | '1' | 'true' | 'false') | null;
        parent_uuid: string | null;
        boat_detail_uuid: string | null;
        is_filepicker: ('0' | '1' | 'true' | 'false') | null;
      }>;
    };
    responses: {
      200: string;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: string; error: string };
    };
  };
  export type put_Folder_upsert = {
    method: 'PUT';
    path: '/folders';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertFolderRequest;
    };
    responses: {
      200: Schemas.Folder;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating or updating the folder.'; error: string };
    };
  };
  export type patch_Folder_update = {
    method: 'PATCH';
    path: '/folders';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertFolderRequest;
    };
    responses: {
      200: Schemas.Folder;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the folder.'; error: string };
    };
  };
  export type get_Folder_show = {
    method: 'GET';
    path: '/folders/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: string;
      404: { message: string };
      422: { message: string };
      500: { message: string; error: string };
    };
  };
  export type delete_Folder_delete = {
    method: 'DELETE';
    path: '/folders/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Folder deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the folder.'; error: string };
    };
  };
  export type post_Follow_follow = {
    method: 'POST';
    path: '/followers';
    requestFormat: 'json';
    parameters: {
      body: Schemas.FollowRequest;
    };
    responses: {
      200: string;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while following.'; error: string };
    };
  };
  export type delete_Follow_unfollow = {
    method: 'DELETE';
    path: '/followers/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Unfollowed successfully.' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while unfollowing.'; error: string };
    };
  };
  export type get_Inventory_index = {
    method: 'GET';
    path: '/expenses';
    requestFormat: 'json';
    parameters: {
      query: Partial<{
        page: number | null;
        page_size: number | null;
        sort_by: string | null;
        sort_order: string | null;
        search: string | null;
        filter: string | null;
        with_disabled: ('0' | '1' | 'true' | 'false') | null;
        boat_detail_uuid: string | null;
        category_uuid: string | null;
        location_uuid: string | null;
        type: string | null;
        start_date: string | null;
        end_date: string | null;
        quantity_min: number | null;
        quantity_max: number | null;
      }>;
    };
    responses: {
      200: string;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: string; error: string };
    };
  };
  export type put_Inventory_upsert = {
    method: 'PUT';
    path: '/expenses';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertInventoryRequest;
    };
    responses: {
      200: Schemas.Inventory;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating or updating the inventory.'; error: string };
    };
  };
  export type patch_Inventory_update = {
    method: 'PATCH';
    path: '/expenses';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertInventoryRequest;
    };
    responses: {
      200: Schemas.Inventory;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the inventory.'; error: string };
    };
  };
  export type get_Inventory_show = {
    method: 'GET';
    path: '/expenses/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: string;
      404: { message: string };
      422: { message: string };
      500: { message: string; error: string };
    };
  };
  export type delete_Inventory_delete = {
    method: 'DELETE';
    path: '/expenses/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Inventory deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the inventory.'; error: string };
    };
  };
  export type put_Inventory_createExpensable = {
    method: 'PUT';
    path: '/expensables';
    requestFormat: 'json';
    parameters: {
      body: Schemas.CreateExpensableRequest;
    };
    responses: {
      200: Schemas.Expensable;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating the expensable relationship.'; error: string };
    };
  };
  export type delete_Inventory_deleteExpensable = {
    method: 'DELETE';
    path: '/expensables/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Expensable relationship deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the expensable relationship.'; error: string };
    };
  };
  export type put_Inventory_assignSubLocationToInventory = {
    method: 'PUT';
    path: '/expense_location';
    requestFormat: 'json';
    parameters: {
      body: Schemas.AssignInventorySubLocationRequest;
    };
    responses: {
      200: Schemas.InventorySubLocation;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while assigning sub-location to inventory.'; error: string };
    };
  };
  export type delete_Inventory_removeSubLocationFromInventory = {
    method: 'DELETE';
    path: '/expense_location/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Sub-location removed from inventory successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while removing sub-location from inventory.'; error: string };
    };
  };
  export type post_InventoryImport_uploadImportFile = {
    method: 'POST';
    path: '/expenses/import-upload';
    requestFormat: 'form-data';
    parameters: {
      body: Schemas.UploadImportFileRequest;
    };
    responses: {
      201: {
        message: 'File uploaded successfully.';
        import: Schemas.Import;
        mapping: {
          valid_rows_count: string;
          invalid_rows_count: string;
          mapped_columns: Array<any>;
          mapped_columns_number: number;
          available_mapping_variables: Array<string> | Array<any>;
          available_variables: string;
          extra_fields: string;
        };
      };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'File upload failed.' };
    };
  };
  export type put_InventoryImport_saveExtraFields = {
    method: 'PUT';
    path: '/expenses/import-extra-fields';
    requestFormat: 'json';
    parameters: {
      body: Schemas.AddInventoryImportExtraFieldsRequest;
    };
    responses: {
      200: Schemas.Import;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: { message: 'Import not found.' };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'Failed to save extra fields.' };
    };
  };
  export type put_InventoryImport_saveMappingFields = {
    method: 'PUT';
    path: '/expenses/import-mapped-fields';
    requestFormat: 'json';
    parameters: {
      body: Schemas.AddInventoryImportMappedFieldsRequest;
    };
    responses: {
      200: { message: 'Field mappings saved successfully.'; import: Schemas.Import };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: { message: 'Import not found.' };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'Failed to save field mappings.' };
    };
  };
  export type post_InventoryImport_processImport = {
    method: 'POST';
    path: '/expenses/import-process/{importUuid}';
    requestFormat: 'json';
    parameters: {
      path: { importUuid: string };
    };
    responses: {
      200: { message: 'Import processing has been started.'; import: string };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: { message: 'Import not found.' };
      409: { message: 'Import has already been processed.' };
      422: { message: 'Import is not ready. Complete field mapping and extra fields first.' };
      500: { message: 'Failed to process import.' };
    };
  };
  export type get_InventoryImport_getStatus = {
    method: 'GET';
    path: '/expenses/import-status/{importUuid}';
    requestFormat: 'json';
    parameters: {
      path: { importUuid: string };
    };
    responses: {
      200: {
        status: string;
        progress: string;
        processed_rows: string;
        total_rows: string;
        error_message: string;
        error_validation: string;
      };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: { message: 'Import not found.' };
      500: { message: 'Failed to retrieve import status.' };
    };
  };
  export type post_Like_like = {
    method: 'POST';
    path: '/comments/{uuid}/like';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: string;
      422: { message: string };
      500: { message: 'An error occurred while liking the comment.'; error: string };
    };
  };
  export type delete_Like_unlike = {
    method: 'DELETE';
    path: '/comments/{uuid}/like';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Unliked successfully.' };
      500: { message: 'An error occurred while unliking the comment.'; error: string };
    };
  };
  export type get_Location_index = {
    method: 'GET';
    path: '/locations';
    requestFormat: 'json';
    parameters: {
      query: Partial<{
        page: number | null;
        page_size: number | null;
        sort_by: string | null;
        sort_order: string | null;
        search: string | null;
        filter: string | null;
        with_disabled: ('0' | '1' | 'true' | 'false') | null;
        is_sub_location: ('0' | '1' | 'true' | 'false') | null;
      }>;
    };
    responses: {
      200: string;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: string; error: string };
    };
  };
  export type put_Location_upsert = {
    method: 'PUT';
    path: '/locations';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertLocationRequest;
    };
    responses: {
      200: Schemas.Location;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating or updating the location.'; error: string };
    };
  };
  export type patch_Location_update = {
    method: 'PATCH';
    path: '/locations';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertLocationRequest;
    };
    responses: {
      200: Schemas.Location;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the location.'; error: string };
    };
  };
  export type get_Location_show = {
    method: 'GET';
    path: '/locations/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: string;
      404: { message: string };
      422: { message: string };
      500: { message: string; error: string };
    };
  };
  export type delete_Location_delete = {
    method: 'DELETE';
    path: '/locations/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Location deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the location.'; error: string };
    };
  };
  export type get_Log_index = {
    method: 'GET';
    path: '/logs';
    requestFormat: 'json';
    parameters: {
      query: Partial<{
        page: number | null;
        page_size: number | null;
        sort_by: string | null;
        sort_order: string | null;
        search: string | null;
        filter: string | null;
        with_disabled: ('0' | '1' | 'true' | 'false') | null;
        type: string | null;
        trip_uuid: string | null;
        boat_detail_uuid: string | null;
        expense_uuid: string | null;
        start_date: string | null;
        end_date: string | null;
        include_autologs: ('0' | '1' | 'true' | 'false') | null;
      }>;
    };
    responses: {
      200: string;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: string; error: string };
    };
  };
  export type put_Log_upsert = {
    method: 'PUT';
    path: '/logs';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertLogRequest;
    };
    responses: {
      200: Schemas.Log;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'Error saving log.'; error: string };
    };
  };
  export type patch_Log_update = {
    method: 'PATCH';
    path: '/logs';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertLogRequest;
    };
    responses: {
      200: Schemas.Log;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while saving the log.'; error: string };
    };
  };
  export type get_Log_show = {
    method: 'GET';
    path: '/logs/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: string;
      404: { message: string };
      422: { message: string };
      500: { message: string; error: string };
    };
  };
  export type delete_Log_delete = {
    method: 'DELETE';
    path: '/logs/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Log deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the log.'; error: string };
    };
  };
  export type post_LogImport_uploadImportFile = {
    method: 'POST';
    path: '/logs/import-upload';
    requestFormat: 'form-data';
    parameters: {
      body: Schemas.UploadImportFileRequest;
    };
    responses: {
      201: {
        message: 'File uploaded successfully.';
        import: Schemas.Import;
        mapping: {
          valid_rows_count: string;
          invalid_rows_count: string;
          mapped_columns: Array<any>;
          mapped_columns_number: number;
          available_mapping_variables: Array<string> | Array<any>;
          available_variables: string;
          extra_fields: string;
        };
      };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'File upload failed.' };
    };
  };
  export type put_LogImport_saveExtraFields = {
    method: 'PUT';
    path: '/logs/import-extra-fields';
    requestFormat: 'json';
    parameters: {
      body: Schemas.AddLogImportExtraFieldsRequest;
    };
    responses: {
      200: Schemas.Import;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: { message: 'Import not found.' };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'Failed to save extra fields.' };
    };
  };
  export type put_LogImport_saveMappingFields = {
    method: 'PUT';
    path: '/logs/import-mapped-fields';
    requestFormat: 'json';
    parameters: {
      body: Schemas.AddLogImportMappedFieldsRequest;
    };
    responses: {
      200: { message: 'Field mappings saved successfully.'; import: Schemas.Import };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: { message: 'Import not found.' };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'Failed to save field mappings.' };
    };
  };
  export type post_LogImport_processImport = {
    method: 'POST';
    path: '/logs/import-process/{importUuid}';
    requestFormat: 'json';
    parameters: {
      path: { importUuid: string };
    };
    responses: {
      200: { message: 'Import processing has been started.'; import: string };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: { message: 'Import not found.' };
      409: { message: 'Import has already been processed.' };
      422: { message: 'Import is not ready. Complete field mapping and extra fields first.' };
      500: { message: 'Failed to process import.' };
    };
  };
  export type get_LogImport_getStatus = {
    method: 'GET';
    path: '/logs/import-status/{importUuid}';
    requestFormat: 'json';
    parameters: {
      path: { importUuid: string };
    };
    responses: {
      200: {
        status: string;
        progress: string;
        processed_rows: string;
        total_rows: string;
        error_message: string;
        error_validation: string;
      };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: { message: 'Import not found.' };
      500: { message: 'Failed to retrieve import status.' };
    };
  };
  export type get_Task_index = {
    method: 'GET';
    path: '/tasks';
    requestFormat: 'json';
    parameters: {
      query: Partial<{
        page: number | null;
        page_size: number | null;
        sort_by: string | null;
        sort_order: string | null;
        search: string | null;
        filter: string | null;
        with_disabled: ('0' | '1' | 'true' | 'false') | null;
        status: string | null;
        priority: number | null;
        assignee_id: number | null;
        boat_detail_uuid: string | null;
        category_uuid: string | null;
        type: ('upcoming' | 'overdue' | 'completed') | null;
        due_date_start: string | null;
        due_date_end: string | null;
      }>;
    };
    responses: {
      200: string;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: string; error: string };
    };
  };
  export type put_Task_upsert = {
    method: 'PUT';
    path: '/tasks';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertTaskRequest;
    };
    responses: {
      200: Schemas.Task;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating or updating the task.'; error: string };
    };
  };
  export type patch_Task_update = {
    method: 'PATCH';
    path: '/tasks';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertTaskRequest;
    };
    responses: {
      200: Schemas.Task;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the task.'; error: string };
    };
  };
  export type get_Task_show = {
    method: 'GET';
    path: '/tasks/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: string;
      404: { message: string };
      422: { message: string };
      500: { message: string; error: string };
    };
  };
  export type delete_Task_delete = {
    method: 'DELETE';
    path: '/tasks/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Task deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the task.'; error: string };
    };
  };
  export type post_TaskImport_uploadImportFile = {
    method: 'POST';
    path: '/tasks/import-upload';
    requestFormat: 'form-data';
    parameters: {
      body: Schemas.UploadImportFileRequest;
    };
    responses: {
      201: {
        message: 'File uploaded successfully.';
        import: Schemas.Import;
        mapping: {
          valid_rows_count: string;
          invalid_rows_count: string;
          mapped_columns: Array<any>;
          mapped_columns_number: number;
          available_mapping_variables: Array<string> | Array<any>;
          available_variables: string;
          extra_fields: string;
        };
      };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'File upload failed.' };
    };
  };
  export type put_TaskImport_saveExtraFields = {
    method: 'PUT';
    path: '/tasks/import-extra-fields';
    requestFormat: 'json';
    parameters: {
      body: Schemas.AddTaskImportExtraFieldsRequest;
    };
    responses: {
      200: Schemas.Import;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: { message: 'Import not found.' };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'Failed to save extra fields.' };
    };
  };
  export type put_TaskImport_saveMappingFields = {
    method: 'PUT';
    path: '/tasks/import-mapped-fields';
    requestFormat: 'json';
    parameters: {
      body: Schemas.AddTaskImportMappedFieldsRequest;
    };
    responses: {
      200: { message: 'Field mappings saved successfully.'; import: Schemas.Import };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: { message: 'Import not found.' };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'Failed to save field mappings.' };
    };
  };
  export type post_TaskImport_processImport = {
    method: 'POST';
    path: '/tasks/import-process/{importUuid}';
    requestFormat: 'json';
    parameters: {
      path: { importUuid: string };
    };
    responses: {
      200: { message: 'Import processing has been started.'; import: string };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: { message: 'Import not found.' };
      409: { message: 'Import has already been processed.' };
      422: { message: 'Import is not ready. Complete field mapping and extra fields first.' };
      500: { message: 'Failed to process import.' };
    };
  };
  export type get_TaskImport_getStatus = {
    method: 'GET';
    path: '/tasks/import-status/{importUuid}';
    requestFormat: 'json';
    parameters: {
      path: { importUuid: string };
    };
    responses: {
      200: {
        status: string;
        progress: string;
        processed_rows: string;
        total_rows: string;
        error_message: string;
        error_validation: string;
      };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      404: { message: 'Import not found.' };
      500: { message: 'Failed to retrieve import status.' };
    };
  };
  export type put_Taxonomy_createTaxonomable = {
    method: 'PUT';
    path: '/taxonomables';
    requestFormat: 'json';
    parameters: {
      body: Schemas.CreateTaxonomableRequest;
    };
    responses: {
      200: Schemas.Taxonomable;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating the taxonomable relationship.'; error: string };
    };
  };
  export type delete_Taxonomy_deleteTaxonomable = {
    method: 'DELETE';
    path: '/taxonomables/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Taxonomable relationship deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the taxonomable relationship.'; error: string };
    };
  };
  export type get_Timezone_index = {
    method: 'GET';
    path: '/timezones';
    requestFormat: 'json';
    parameters: never;
    responses: {
      200: Array<Schemas.TimezoneResource>;
      422: { message: string };
      500: { message: string; error: string };
    };
  };
  export type get_Trip_index = {
    method: 'GET';
    path: '/trips';
    requestFormat: 'json';
    parameters: {
      query: Partial<{
        page: number | null;
        page_size: number | null;
        sort_by: string | null;
        sort_order: string | null;
        search: string | null;
        filter: string | null;
        with_disabled: ('0' | '1' | 'true' | 'false') | null;
        active: ('0' | '1' | 'true' | 'false') | null;
        boat_detail_uuid: string | null;
        start_date: string | null;
        end_date: string | null;
      }>;
    };
    responses: {
      200: string;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: string; error: string };
    };
  };
  export type put_Trip_upsert = {
    method: 'PUT';
    path: '/trips';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertTripRequest;
    };
    responses: {
      200: Schemas.Trip;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while creating or updating the trip.'; error: string };
    };
  };
  export type patch_Trip_update = {
    method: 'PATCH';
    path: '/trips';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpsertTripRequest;
    };
    responses: {
      200: Schemas.Trip;
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the trip.'; error: string };
    };
  };
  export type get_Trip_show = {
    method: 'GET';
    path: '/trips/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: string;
      404: { message: string };
      422: { message: string };
      500: { message: string; error: string };
    };
  };
  export type delete_Trip_delete = {
    method: 'DELETE';
    path: '/trips/{uuid}';
    requestFormat: 'json';
    parameters: {
      path: { uuid: string };
    };
    responses: {
      200: { message: 'Trip deleted successfully' };
      403: {
        /**
         * Error overview.
         */
        message: string;
      };
      500: { message: 'An error occurred while deleting the trip.'; error: string };
    };
  };
  export type get_User_me = {
    method: 'GET';
    path: '/users/me';
    requestFormat: 'json';
    parameters: never;
    responses: {
      200: Schemas.UserResource;
      500: { message: 'An error occurred while fetching the user.'; error: string };
    };
  };
  export type post_User_setupUser = {
    method: 'POST';
    path: '/users/setup';
    requestFormat: 'json';
    parameters: {
      body: Schemas.SetupUserRequest;
    };
    responses: {
      200: { message: 'User setup completed successfully.' };
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred during user setup.'; error: string };
    };
  };
  export type put_User_completeSetupUser = {
    method: 'PUT';
    path: '/users/complete_setup';
    requestFormat: 'json';
    parameters: never;
    responses: {
      200: { message: 'User setup completed successfully.' };
      500: { message: 'An error occurred during user setup completion.'; error: string };
    };
  };
  export type patch_User_updateProfile = {
    method: 'PATCH';
    path: '/users/profile';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpdateProfileRequest;
    };
    responses: {
      200: Schemas.UserResource;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating user profile.'; error: string };
    };
  };
  export type get_User_settings = {
    method: 'GET';
    path: '/users/settings';
    requestFormat: 'json';
    parameters: never;
    responses: {
      200: Schemas.SettingsResource;
      500: { message: 'An error occurred while fetching the user settings.'; error: string };
    };
  };
  export type patch_User_updateSettings = {
    method: 'PATCH';
    path: '/users/settings';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpdateSettingsRequest;
    };
    responses: {
      200: Schemas.SettingsResource;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating user settings.'; error: string };
    };
  };
  export type put_User_updateAddress = {
    method: 'PUT';
    path: '/users/address';
    requestFormat: 'json';
    parameters: {
      body: Schemas.UpdateAddressRequest;
    };
    responses: {
      200: Schemas.AddressResource;
      422: {
        /**
         * Errors overview.
         */
        message: string;
        /**
         * A detailed description of each field that failed validation.
         */
        errors: Record<string, Array<string>>;
      };
      500: { message: 'An error occurred while updating the address.'; error: string };
    };
  };
  export type get_User_getRelatedUsers = {
    method: 'GET';
    path: '/users/related';
    requestFormat: 'json';
    parameters: never;
    responses: {
      200: Array<Schemas.RelatedUserResource>;
      500: { message: 'An error occurred while fetching the related users.'; error: string };
    };
  };

  // </Endpoints>
}

// <EndpointByMethod>
export type EndpointByMethod = {
  get: {
    '/activity-logs': Endpoints.get_ActivityLog_getActivityLogs;
    '/activity-logs/{boatUuid}': Endpoints.get_ActivityLog_getActivityLogsByBoat;
    '/alerts': Endpoints.get_Alert_index;
    '/alerts/{uuid}': Endpoints.get_Alert_show;
    '/boat_details': Endpoints.get_BoatDetails_index;
    '/boat_details/{uuid}': Endpoints.get_BoatDetails_show;
    '/categories': Endpoints.get_Category_index;
    '/categories/{uuid}': Endpoints.get_Category_show;
    '/assistant/connector': Endpoints.get_Chat_connector;
    '/checklists/featured': Endpoints.get_Checklist_getFeaturedPublicChecklists;
    '/checklists/public/{uuid}': Endpoints.get_Checklist_getPublicChecklist;
    '/checklists': Endpoints.get_Checklist_index;
    '/checklists/{uuid}': Endpoints.get_Checklist_show;
    '/checklists/public': Endpoints.get_Checklist_getPublicChecklists;
    '/costs': Endpoints.get_Cost_index;
    '/costs/{uuid}': Endpoints.get_Cost_show;
    '/countries': Endpoints.get_Country_index;
    '/currencies': Endpoints.get_Currency_index;
    '/notifications/device-tokens': Endpoints.get_DeviceToken_index;
    '/exports/{export}': Endpoints.get_Export_show;
    '/exports/{export}/download': Endpoints.get_Exports_download;
    '/favorites': Endpoints.get_Favorite_index;
    '/files/public/{fileUuid}': Endpoints.get_File_getPublicFile;
    '/files': Endpoints.get_File_index;
    '/files/{uuid}/details': Endpoints.get_File_show;
    '/files/{fileUuid}': Endpoints.get_File_download;
    '/folders': Endpoints.get_Folder_index;
    '/folders/{uuid}': Endpoints.get_Folder_show;
    '/expenses': Endpoints.get_Inventory_index;
    '/expenses/{uuid}': Endpoints.get_Inventory_show;
    '/expenses/import-status/{importUuid}': Endpoints.get_InventoryImport_getStatus;
    '/locations': Endpoints.get_Location_index;
    '/locations/{uuid}': Endpoints.get_Location_show;
    '/logs': Endpoints.get_Log_index;
    '/logs/{uuid}': Endpoints.get_Log_show;
    '/logs/import-status/{importUuid}': Endpoints.get_LogImport_getStatus;
    '/tasks': Endpoints.get_Task_index;
    '/tasks/{uuid}': Endpoints.get_Task_show;
    '/tasks/import-status/{importUuid}': Endpoints.get_TaskImport_getStatus;
    '/timezones': Endpoints.get_Timezone_index;
    '/trips': Endpoints.get_Trip_index;
    '/trips/{uuid}': Endpoints.get_Trip_show;
    '/users/me': Endpoints.get_User_me;
    '/users/settings': Endpoints.get_User_settings;
    '/users/related': Endpoints.get_User_getRelatedUsers;
  };
  patch: {
    '/alerts': Endpoints.patch_Alert_update;
    '/boats': Endpoints.patch_Boat_update;
    '/boat_details': Endpoints.patch_BoatDetails_update;
    '/categories': Endpoints.patch_Category_update;
    '/checklists': Endpoints.patch_Checklist_update;
    '/checklist_items': Endpoints.patch_ChecklistItem_updateItem;
    '/comments/{uuid}': Endpoints.patch_Comment_update;
    '/costs': Endpoints.patch_Cost_update;
    '/fields': Endpoints.patch_Field_update;
    '/field_values': Endpoints.patch_Field_updateValue;
    '/files': Endpoints.patch_File_update;
    '/folders': Endpoints.patch_Folder_update;
    '/expenses': Endpoints.patch_Inventory_update;
    '/locations': Endpoints.patch_Location_update;
    '/logs': Endpoints.patch_Log_update;
    '/tasks': Endpoints.patch_Task_update;
    '/trips': Endpoints.patch_Trip_update;
    '/users/profile': Endpoints.patch_User_updateProfile;
    '/users/settings': Endpoints.patch_User_updateSettings;
  };
  post: {
    '/blockers': Endpoints.post_Block_block;
    '/webhooks/sanity/boat-models': Endpoints.post_BoatModelWebhook_handle;
    '/assistant/chat': Endpoints.post_Chat_chat;
    '/webhooks/clerk': Endpoints.post_ClerkWebhook_handle;
    '/comments': Endpoints.post_Comment_store;
    '/notifications/device-tokens': Endpoints.post_DeviceToken_store;
    '/exports': Endpoints.post_Export_create;
    '/favorites': Endpoints.post_Favorite_store;
    '/files/presign': Endpoints.post_File_presign;
    '/files/confirm': Endpoints.post_File_confirm;
    '/followers': Endpoints.post_Follow_follow;
    '/expenses/import-upload': Endpoints.post_InventoryImport_uploadImportFile;
    '/expenses/import-process/{importUuid}': Endpoints.post_InventoryImport_processImport;
    '/comments/{uuid}/like': Endpoints.post_Like_like;
    '/logs/import-upload': Endpoints.post_LogImport_uploadImportFile;
    '/logs/import-process/{importUuid}': Endpoints.post_LogImport_processImport;
    '/tasks/import-upload': Endpoints.post_TaskImport_uploadImportFile;
    '/tasks/import-process/{importUuid}': Endpoints.post_TaskImport_processImport;
    '/users/setup': Endpoints.post_User_setupUser;
  };
  delete: {
    '/blockers/{uuid}': Endpoints.delete_Block_unblock;
    '/boats/{uuid}': Endpoints.delete_Boat_delete;
    '/boat_details/{uuid}': Endpoints.delete_BoatDetails_delete;
    '/categories/{uuid}': Endpoints.delete_Category_delete;
    '/checklists/{uuid}': Endpoints.delete_Checklist_delete;
    '/checklistables/{uuid}': Endpoints.delete_Checklist_deleteChecklistable;
    '/checklist_items/{uuid}': Endpoints.delete_ChecklistItem_deleteItem;
    '/checklist_item_check/{uuid}': Endpoints.delete_ChecklistItem_uncheckItem;
    '/comments/{uuid}': Endpoints.delete_Comment_delete;
    '/costs/{uuid}': Endpoints.delete_Cost_delete;
    '/costables/{uuid}': Endpoints.delete_Cost_deleteCostable;
    '/notifications/device-tokens': Endpoints.delete_DeviceToken_destroy;
    '/favorites/{uuid}': Endpoints.delete_Favorite_destroy;
    '/fields/{uuid}': Endpoints.delete_Field_delete;
    '/field_values/{uuid}': Endpoints.delete_Field_deleteValue;
    '/files/{uuid}': Endpoints.delete_File_delete;
    '/fileables/{uuid}': Endpoints.delete_File_deleteFileable;
    '/folders/{uuid}': Endpoints.delete_Folder_delete;
    '/followers/{uuid}': Endpoints.delete_Follow_unfollow;
    '/expenses/{uuid}': Endpoints.delete_Inventory_delete;
    '/expensables/{uuid}': Endpoints.delete_Inventory_deleteExpensable;
    '/expense_location/{uuid}': Endpoints.delete_Inventory_removeSubLocationFromInventory;
    '/comments/{uuid}/like': Endpoints.delete_Like_unlike;
    '/locations/{uuid}': Endpoints.delete_Location_delete;
    '/logs/{uuid}': Endpoints.delete_Log_delete;
    '/tasks/{uuid}': Endpoints.delete_Task_delete;
    '/taxonomables/{uuid}': Endpoints.delete_Taxonomy_deleteTaxonomable;
    '/trips/{uuid}': Endpoints.delete_Trip_delete;
  };
  put: {
    '/boats': Endpoints.put_Boat_upsert;
    '/boat_details': Endpoints.put_BoatDetails_upsert;
    '/boat_models': Endpoints.put_BoatModel_upsert;
    '/categories': Endpoints.put_Category_upsert;
    '/checklists': Endpoints.put_Checklist_upsert;
    '/checklistables': Endpoints.put_Checklist_createChecklistable;
    '/checklist_items': Endpoints.put_ChecklistItem_upsertItem;
    '/checklist_item_check': Endpoints.put_ChecklistItem_checkItem;
    '/costs': Endpoints.put_Cost_upsert;
    '/costables': Endpoints.put_Cost_createCostable;
    '/fields': Endpoints.put_Field_upsert;
    '/field_values': Endpoints.put_Field_upsertValue;
    '/fileables': Endpoints.put_File_createFileable;
    '/folders': Endpoints.put_Folder_upsert;
    '/expenses': Endpoints.put_Inventory_upsert;
    '/expensables': Endpoints.put_Inventory_createExpensable;
    '/expense_location': Endpoints.put_Inventory_assignSubLocationToInventory;
    '/expenses/import-extra-fields': Endpoints.put_InventoryImport_saveExtraFields;
    '/expenses/import-mapped-fields': Endpoints.put_InventoryImport_saveMappingFields;
    '/locations': Endpoints.put_Location_upsert;
    '/logs': Endpoints.put_Log_upsert;
    '/logs/import-extra-fields': Endpoints.put_LogImport_saveExtraFields;
    '/logs/import-mapped-fields': Endpoints.put_LogImport_saveMappingFields;
    '/tasks': Endpoints.put_Task_upsert;
    '/tasks/import-extra-fields': Endpoints.put_TaskImport_saveExtraFields;
    '/tasks/import-mapped-fields': Endpoints.put_TaskImport_saveMappingFields;
    '/taxonomables': Endpoints.put_Taxonomy_createTaxonomable;
    '/trips': Endpoints.put_Trip_upsert;
    '/users/complete_setup': Endpoints.put_User_completeSetupUser;
    '/users/address': Endpoints.put_User_updateAddress;
  };
};

// </EndpointByMethod>

// <EndpointByMethod.Shorthands>
export type GetEndpoints = EndpointByMethod['get'];
export type PatchEndpoints = EndpointByMethod['patch'];
export type PostEndpoints = EndpointByMethod['post'];
export type DeleteEndpoints = EndpointByMethod['delete'];
export type PutEndpoints = EndpointByMethod['put'];
// </EndpointByMethod.Shorthands>

// <ApiClientTypes>
export type EndpointParameters = {
  body?: unknown;
  query?: Record<string, unknown>;
  header?: Record<string, unknown>;
  path?: Record<string, unknown>;
};

export type MutationMethod = 'post' | 'put' | 'patch' | 'delete';
export type Method = 'get' | 'head' | 'options' | MutationMethod;

type RequestFormat = 'json' | 'form-data' | 'form-url' | 'binary' | 'text';

export type DefaultEndpoint = {
  parameters?: EndpointParameters | undefined;
  responses?: Record<string, unknown>;
  responseHeaders?: Record<string, unknown>;
};

export type Endpoint<TConfig extends DefaultEndpoint = DefaultEndpoint> = {
  operationId: string;
  method: Method;
  path: string;
  requestFormat: RequestFormat;
  parameters?: TConfig['parameters'];
  meta: {
    alias: string;
    hasParameters: boolean;
    areParametersRequired: boolean;
  };
  responses?: TConfig['responses'];
  responseHeaders?: TConfig['responseHeaders'];
};

export interface Fetcher {
  decodePathParams?: (path: string, pathParams: Record<string, string>) => string;
  encodeSearchParams?: (searchParams: Record<string, unknown> | undefined) => URLSearchParams;
  //
  fetch: (input: {
    method: Method;
    url: URL;
    urlSearchParams?: URLSearchParams | undefined;
    parameters?: EndpointParameters | undefined;
    path: string;
    overrides?: RequestInit;
    throwOnStatusError?: boolean;
  }) => Promise<Response>;
  parseResponseData?: (response: Response) => Promise<unknown>;
}

export const successStatusCodes = [
  200, 201, 202, 203, 204, 205, 206, 207, 208, 226, 300, 301, 302, 303, 304, 305, 306, 307, 308,
] as const;
export type SuccessStatusCode = (typeof successStatusCodes)[number];

export const errorStatusCodes = [
  400, 401, 402, 403, 404, 405, 406, 407, 408, 409, 410, 411, 412, 413, 414, 415, 416, 417, 418, 421, 422, 423, 424,
  425, 426, 428, 429, 431, 451, 500, 501, 502, 503, 504, 505, 506, 507, 508, 510, 511,
] as const;
export type ErrorStatusCode = (typeof errorStatusCodes)[number];

// Taken from https://github.com/unjs/fetchdts/blob/ec4eaeab5d287116171fc1efd61f4a1ad34e4609/src/fetch.ts#L3
export interface TypedHeaders<TypedHeaderValues extends Record<string, string> | unknown>
  extends Omit<Headers, 'append' | 'delete' | 'get' | 'getSetCookie' | 'has' | 'set' | 'forEach'> {
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/Headers/append) */
  append: <Name extends Extract<keyof TypedHeaderValues, string> | (string & {})>(
    name: Name,
    value: Lowercase<Name> extends keyof TypedHeaderValues ? TypedHeaderValues[Lowercase<Name>] : string,
  ) => void;
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/Headers/delete) */
  delete: <Name extends Extract<keyof TypedHeaderValues, string> | (string & {})>(name: Name) => void;
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/Headers/get) */
  get: <Name extends Extract<keyof TypedHeaderValues, string> | (string & {})>(
    name: Name,
  ) => (Lowercase<Name> extends keyof TypedHeaderValues ? TypedHeaderValues[Lowercase<Name>] : string) | null;
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/Headers/getSetCookie) */
  getSetCookie: () => string[];
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/Headers/has) */
  has: <Name extends Extract<keyof TypedHeaderValues, string> | (string & {})>(name: Name) => boolean;
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/Headers/set) */
  set: <Name extends Extract<keyof TypedHeaderValues, string> | (string & {})>(
    name: Name,
    value: Lowercase<Name> extends keyof TypedHeaderValues ? TypedHeaderValues[Lowercase<Name>] : string,
  ) => void;
  forEach: (
    callbackfn: (
      value: TypedHeaderValues[keyof TypedHeaderValues] | (string & {}),
      key: Extract<keyof TypedHeaderValues, string> | (string & {}),
      parent: TypedHeaders<TypedHeaderValues>,
    ) => void,
    thisArg?: any,
  ) => void;
}

/** @see https://developer.mozilla.org/en-US/docs/Web/API/Response */
export interface TypedSuccessResponse<TSuccess, TStatusCode, THeaders>
  extends Omit<Response, 'ok' | 'status' | 'json' | 'headers'> {
  ok: true;
  status: TStatusCode;
  headers: never extends THeaders ? Headers : TypedHeaders<THeaders>;
  data: TSuccess;
  /** [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/API/Response/json) */
  json: () => Promise<TSuccess>;
}

/** @see https://developer.mozilla.org/en-US/docs/Web/API/Response */
export interface TypedErrorResponse<TData, TStatusCode, THeaders>
  extends Omit<Response, 'ok' | 'status' | 'json' | 'headers'> {
  ok: false;
  status: TStatusCode;
  headers: never extends THeaders ? Headers : TypedHeaders<THeaders>;
  data: TData;
  /** [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/API/Response/json) */
  json: () => Promise<TData>;
}

export type TypedApiResponse<TAllResponses extends Record<string | number, unknown> = {}, THeaders = {}> = {
  [K in keyof TAllResponses]: K extends string
    ? K extends `${infer TStatusCode extends number}`
      ? TStatusCode extends SuccessStatusCode
        ? TypedSuccessResponse<TAllResponses[K], TStatusCode, K extends keyof THeaders ? THeaders[K] : never>
        : TypedErrorResponse<TAllResponses[K], TStatusCode, K extends keyof THeaders ? THeaders[K] : never>
      : never
    : K extends number
      ? K extends SuccessStatusCode
        ? TypedSuccessResponse<TAllResponses[K], K, K extends keyof THeaders ? THeaders[K] : never>
        : TypedErrorResponse<TAllResponses[K], K, K extends keyof THeaders ? THeaders[K] : never>
      : never;
}[keyof TAllResponses];

export type SafeApiResponse<TEndpoint> = TEndpoint extends { responses: infer TResponses }
  ? TResponses extends Record<string, unknown>
    ? TypedApiResponse<TResponses, TEndpoint extends { responseHeaders: infer THeaders } ? THeaders : never>
    : never
  : never;

export type InferResponseByStatus<TEndpoint, TStatusCode> = Extract<
  SafeApiResponse<TEndpoint>,
  { status: TStatusCode }
>;

type RequiredKeys<T> = {
  [P in keyof T]-?: undefined extends T[P] ? never : P;
}[keyof T];

type MaybeOptionalArg<T> = RequiredKeys<T> extends never ? [config?: T] : [config: T];
type NotNever<T> = [T] extends [never] ? false : true;

// </ApiClientTypes>

// <TypedStatusError>
export class TypedStatusError<TData = unknown> extends Error {
  response: TypedErrorResponse<TData, ErrorStatusCode, unknown>;
  status: number;
  constructor(response: TypedErrorResponse<TData, ErrorStatusCode, unknown>) {
    super(`HTTP ${response.status}: ${response.statusText}`);
    this.name = 'TypedStatusError';
    this.response = response;
    this.status = response.status;
  }
}
// </TypedStatusError>

// <ApiClient>
export class ApiClient {
  baseUrl: string = '';
  successStatusCodes = successStatusCodes;
  errorStatusCodes = errorStatusCodes;

  constructor(public fetcher: Fetcher) {}

  setBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl;
    return this;
  }

  /**
   * Replace path parameters in URL
   * Supports both OpenAPI format {param} and Express format :param
   */
  defaultDecodePathParams = (url: string, params: Record<string, string>): string => {
    return url
      .replace(/{(\w+)}/g, (_, key: string) => params[key] || `{${key}}`)
      .replace(/:([a-zA-Z0-9_]+)/g, (_, key: string) => params[key] || `:${key}`);
  };

  /** Uses URLSearchParams, skips null/undefined values */
  defaultEncodeSearchParams = (queryParams: Record<string, unknown> | undefined): URLSearchParams | undefined => {
    if (!queryParams) return;

    const searchParams = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value != null) {
        // Skip null/undefined values
        if (Array.isArray(value)) {
          value.forEach((val) => val != null && searchParams.append(key, String(val)));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });

    return searchParams;
  };

  defaultParseResponseData = async (response: Response): Promise<unknown> => {
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.startsWith('text/')) {
      return await response.text();
    }

    if (contentType === 'application/octet-stream') {
      return await response.arrayBuffer();
    }

    if (
      contentType.includes('application/json') ||
      (contentType.includes('application/') && contentType.includes('json')) ||
      contentType === '*/*'
    ) {
      try {
        return await response.json();
      } catch {
        return undefined;
      }
    }

    return;
  };

  // <ApiClient.get>
  get<Path extends keyof GetEndpoints, TEndpoint extends GetEndpoints[Path]>(
    path: Path,
    ...params: MaybeOptionalArg<
      TEndpoint extends { parameters: infer UParams }
        ? NotNever<UParams> extends true
          ? UParams & { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
          : { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
        : { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
    >
  ): Promise<Extract<InferResponseByStatus<TEndpoint, SuccessStatusCode>, { data: {} }>['data']>;

  get<Path extends keyof GetEndpoints, TEndpoint extends GetEndpoints[Path]>(
    path: Path,
    ...params: MaybeOptionalArg<
      TEndpoint extends { parameters: infer UParams }
        ? NotNever<UParams> extends true
          ? UParams & { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
          : { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
        : { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
    >
  ): Promise<SafeApiResponse<TEndpoint>>;

  get<Path extends keyof GetEndpoints, _TEndpoint extends GetEndpoints[Path]>(
    path: Path,
    ...params: MaybeOptionalArg<any>
  ): Promise<any> {
    return this.request('get', path, ...params);
  }
  // </ApiClient.get>

  // <ApiClient.patch>
  patch<Path extends keyof PatchEndpoints, TEndpoint extends PatchEndpoints[Path]>(
    path: Path,
    ...params: MaybeOptionalArg<
      TEndpoint extends { parameters: infer UParams }
        ? NotNever<UParams> extends true
          ? UParams & { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
          : { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
        : { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
    >
  ): Promise<Extract<InferResponseByStatus<TEndpoint, SuccessStatusCode>, { data: {} }>['data']>;

  patch<Path extends keyof PatchEndpoints, TEndpoint extends PatchEndpoints[Path]>(
    path: Path,
    ...params: MaybeOptionalArg<
      TEndpoint extends { parameters: infer UParams }
        ? NotNever<UParams> extends true
          ? UParams & { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
          : { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
        : { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
    >
  ): Promise<SafeApiResponse<TEndpoint>>;

  patch<Path extends keyof PatchEndpoints, _TEndpoint extends PatchEndpoints[Path]>(
    path: Path,
    ...params: MaybeOptionalArg<any>
  ): Promise<any> {
    return this.request('patch', path, ...params);
  }
  // </ApiClient.patch>

  // <ApiClient.post>
  post<Path extends keyof PostEndpoints, TEndpoint extends PostEndpoints[Path]>(
    path: Path,
    ...params: MaybeOptionalArg<
      TEndpoint extends { parameters: infer UParams }
        ? NotNever<UParams> extends true
          ? UParams & { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
          : { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
        : { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
    >
  ): Promise<Extract<InferResponseByStatus<TEndpoint, SuccessStatusCode>, { data: {} }>['data']>;

  post<Path extends keyof PostEndpoints, TEndpoint extends PostEndpoints[Path]>(
    path: Path,
    ...params: MaybeOptionalArg<
      TEndpoint extends { parameters: infer UParams }
        ? NotNever<UParams> extends true
          ? UParams & { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
          : { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
        : { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
    >
  ): Promise<SafeApiResponse<TEndpoint>>;

  post<Path extends keyof PostEndpoints, _TEndpoint extends PostEndpoints[Path]>(
    path: Path,
    ...params: MaybeOptionalArg<any>
  ): Promise<any> {
    return this.request('post', path, ...params);
  }
  // </ApiClient.post>

  // <ApiClient.delete>
  delete<Path extends keyof DeleteEndpoints, TEndpoint extends DeleteEndpoints[Path]>(
    path: Path,
    ...params: MaybeOptionalArg<
      TEndpoint extends { parameters: infer UParams }
        ? NotNever<UParams> extends true
          ? UParams & { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
          : { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
        : { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
    >
  ): Promise<Extract<InferResponseByStatus<TEndpoint, SuccessStatusCode>, { data: {} }>['data']>;

  delete<Path extends keyof DeleteEndpoints, TEndpoint extends DeleteEndpoints[Path]>(
    path: Path,
    ...params: MaybeOptionalArg<
      TEndpoint extends { parameters: infer UParams }
        ? NotNever<UParams> extends true
          ? UParams & { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
          : { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
        : { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
    >
  ): Promise<SafeApiResponse<TEndpoint>>;

  delete<Path extends keyof DeleteEndpoints, _TEndpoint extends DeleteEndpoints[Path]>(
    path: Path,
    ...params: MaybeOptionalArg<any>
  ): Promise<any> {
    return this.request('delete', path, ...params);
  }
  // </ApiClient.delete>

  // <ApiClient.put>
  put<Path extends keyof PutEndpoints, TEndpoint extends PutEndpoints[Path]>(
    path: Path,
    ...params: MaybeOptionalArg<
      TEndpoint extends { parameters: infer UParams }
        ? NotNever<UParams> extends true
          ? UParams & { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
          : { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
        : { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
    >
  ): Promise<Extract<InferResponseByStatus<TEndpoint, SuccessStatusCode>, { data: {} }>['data']>;

  put<Path extends keyof PutEndpoints, TEndpoint extends PutEndpoints[Path]>(
    path: Path,
    ...params: MaybeOptionalArg<
      TEndpoint extends { parameters: infer UParams }
        ? NotNever<UParams> extends true
          ? UParams & { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
          : { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
        : { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
    >
  ): Promise<SafeApiResponse<TEndpoint>>;

  put<Path extends keyof PutEndpoints, _TEndpoint extends PutEndpoints[Path]>(
    path: Path,
    ...params: MaybeOptionalArg<any>
  ): Promise<any> {
    return this.request('put', path, ...params);
  }
  // </ApiClient.put>

  // <ApiClient.request>
  /**
   * Generic request method with full type-safety for any endpoint
   */
  request<
    TMethod extends keyof EndpointByMethod,
    TPath extends keyof EndpointByMethod[TMethod],
    TEndpoint extends EndpointByMethod[TMethod][TPath],
  >(
    method: TMethod,
    path: TPath,
    ...params: MaybeOptionalArg<
      TEndpoint extends { parameters: infer UParams }
        ? NotNever<UParams> extends true
          ? UParams & { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
          : { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
        : { overrides?: RequestInit; withResponse?: false; throwOnStatusError?: boolean }
    >
  ): Promise<Extract<InferResponseByStatus<TEndpoint, SuccessStatusCode>, { data: {} }>['data']>;

  request<
    TMethod extends keyof EndpointByMethod,
    TPath extends keyof EndpointByMethod[TMethod],
    TEndpoint extends EndpointByMethod[TMethod][TPath],
  >(
    method: TMethod,
    path: TPath,
    ...params: MaybeOptionalArg<
      TEndpoint extends { parameters: infer UParams }
        ? NotNever<UParams> extends true
          ? UParams & { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
          : { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
        : { overrides?: RequestInit; withResponse?: true; throwOnStatusError?: boolean }
    >
  ): Promise<SafeApiResponse<TEndpoint>>;

  request<
    TMethod extends keyof EndpointByMethod,
    TPath extends keyof EndpointByMethod[TMethod],
    TEndpoint extends EndpointByMethod[TMethod][TPath],
  >(method: TMethod, path: TPath, ...params: MaybeOptionalArg<any>): Promise<any> {
    const requestParams = params[0];
    const withResponse = requestParams?.withResponse;
    const {
      withResponse: _,
      throwOnStatusError = withResponse ? false : true,
      overrides,
      ...fetchParams
    } = requestParams || {};

    const parametersToSend: EndpointParameters = {};
    if (requestParams?.body !== undefined) (parametersToSend as any).body = requestParams.body;
    if (requestParams?.query !== undefined) (parametersToSend as any).query = requestParams.query;
    if (requestParams?.header !== undefined) (parametersToSend as any).header = requestParams.header;
    if (requestParams?.path !== undefined) (parametersToSend as any).path = requestParams.path;

    const resolvedPath = (this.fetcher.decodePathParams ?? this.defaultDecodePathParams)(
      this.baseUrl + (path as string),
      (parametersToSend.path ?? {}) as Record<string, string>,
    );
    const url = new URL(resolvedPath);
    const urlSearchParams = (this.fetcher.encodeSearchParams ?? this.defaultEncodeSearchParams)(parametersToSend.query);

    const promise = this.fetcher
      .fetch({
        method: method,
        path: path as string,
        url,
        urlSearchParams,
        parameters: Object.keys(fetchParams).length ? fetchParams : undefined,
        overrides,
        throwOnStatusError,
      })
      .then(async (response) => {
        const data = await (this.fetcher.parseResponseData ?? this.defaultParseResponseData)(response);
        const typedResponse = Object.assign(response, {
          data: data,
          json: () => Promise.resolve(data),
        }) as SafeApiResponse<TEndpoint>;

        if (throwOnStatusError && errorStatusCodes.includes(response.status as never)) {
          throw new TypedStatusError(typedResponse as never);
        }

        return withResponse ? typedResponse : data;
      });

    return promise as Extract<InferResponseByStatus<TEndpoint, SuccessStatusCode>, { data: {} }>['data'];
  }
  // </ApiClient.request>
}

export function createApiClient(fetcher: Fetcher, baseUrl?: string) {
  return new ApiClient(fetcher).setBaseUrl(baseUrl ?? '');
}

/**
 Example usage:
 const api = createApiClient((method, url, params) =>
   fetch(url, { method, body: JSON.stringify(params) }).then((res) => res.json()),
 );
 api.get("/users").then((users) => console.log(users));
 api.post("/users", { body: { name: "John" } }).then((user) => console.log(user));
 api.put("/users/:id", { path: { id: 1 }, body: { name: "John" } }).then((user) => console.log(user));

 // With error handling
 const result = await api.get("/users/{id}", { path: { id: "123" }, withResponse: true });
 if (result.ok) {
   // Access data directly
   const user = result.data;
   console.log(user);

   // Or use the json() method for compatibility
   const userFromJson = await result.json();
   console.log(userFromJson);
 } else {
   const error = result.data;
   console.error(`Error ${result.status}:`, error);
 }
*/

// </ApiClient>
